import React, { useRef, useState, useEffect, useMemo, useCallback, forwardRef, useImperativeHandle } from 'react'
import 'video.js/dist/video-js.css'
import { Participant } from '../types'
import { SubtitleCue } from './SubtitleParser'
import { useSubtitles, formatAddedDate, normalizeSubtitleTracks, type SubtitleTrackOption } from './useSubtitles'
import SubtitleOverlay from './SubtitleOverlay'
import CompassWithParticipants, { compassSizeFor, CONE_DISC } from './CompassWithParticipants'
import { playbarVariant, PLAYBAR_GREEN, PLAYBAR_BLUE, viewButtonVariant } from './designPreview'
import ViewEyeDisc from './ViewEyeDisc'
import { SKINS, skinFromQuery, PLAYER_FONT } from './skin'
import TranscriptPanel, { type TranscriptLanguage } from './TranscriptPanel'
import TitleOverlay from './TitleOverlay'
import ZoomReadout, { zoomReadoutEnabled } from './ZoomReadout'
import QualityReadout, { qualityReadoutEnabled, bigVideoHost, pinnedRendition } from './QualityReadout'
import FlatSurface from './surfaces/FlatSurface'
import Sphere360Surface from './surfaces/Sphere360Surface'
import PlayerGlyph from './PlayerGlyph'
import { Sphere360Handle, QualityLevelInfo } from './surfaceTypes'
import { qualityLabel } from './qualityLevels'
import { tierTargets, QUALITY_TIER_LABELS, type QualityTier } from './qualityTiers'
import { PlaybackCore } from './core/PlaybackCore'
import { SPLIT_LAYOUTS, lockedHeadings, resolveFourView, resolvePaneLayout, splitLayoutFromParam, hasAllViews, type SplitLayout } from './splitLayout'
import LayoutGlyph from './LayoutGlyph'
import { surfaceForView, PlayerView } from './playerView'
import type { PaneIndex } from './VideoPlayer360Controller'
import { detectCapabilities, readEnvironment } from './capabilities/detectCapabilities'
import { resolveFeatures, resolveControls, initialTextureLive, shouldVerifySphere, staticLikely360 } from './capabilities/resolveFeatures'
import { getCachedTextureLive, setCachedTextureLive, clearCachedTextureLive } from './capabilities/capabilityCache'
import { probeTexture, webglProbeDeps } from './capabilities/probeTexture'
import styles from './styles'
import { usePlayerLayout } from './usePlayerLayout'
import {
  SUBTITLE_SIZE_PRESETS,
  SubtitleSizePreset,
  migrateSubtitleScale,
  gridSidebarWidth,
  fitControlRow,
  fitTopRightCluster,
  CLUSTER_INSET,
  MIN_COMPASS,
} from './layout'
import { attachSegmentVerifier, type SegmentSource, type VerifiablePlayer } from '../verification/SegmentVerifier'
import { failureScrimStyle } from '../verification/failureScrim'
import { prefersReducedMotion } from '../verification/verificationTheme'
import type { VerificationHalt } from '../verification/useVideoVerification'

// MUI
import Tooltip from '@mui/material/Tooltip'

// MUI Icons
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import PauseIcon from '@mui/icons-material/Pause'
import VolumeUpIcon from '@mui/icons-material/VolumeUp'
import VolumeOffIcon from '@mui/icons-material/VolumeOff'
import FullscreenIcon from '@mui/icons-material/Fullscreen'
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit'
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked'
import VerticalAlignTopIcon from '@mui/icons-material/VerticalAlignTop'
import VerticalAlignBottomIcon from '@mui/icons-material/VerticalAlignBottom'
import SpatialAudioIcon from '@mui/icons-material/SpatialAudio'
import SpatialAudioOffIcon from '@mui/icons-material/SpatialAudioOff'
import SubtitlesIcon from '@mui/icons-material/Subtitles'
import ClosedCaptionDisabledIcon from '@mui/icons-material/ClosedCaptionDisabled'
import GridViewIcon from '@mui/icons-material/GridView'
import ScreenRotationIcon from '@mui/icons-material/ScreenRotation'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ScreenRotationOutlinedIcon from '@mui/icons-material/ScreenRotationOutlined'
import SettingsIcon from '@mui/icons-material/Settings'
import Replay10Icon from '@mui/icons-material/Replay10'
import Forward10Icon from '@mui/icons-material/Forward10'
import { languageName } from './languageNames'

// Guarded player diagnostics. Enabled with ?debug=true; no-op otherwise.
const swapDebug = (() => {
  try {
    return new URLSearchParams(window.location.search).get('debug') === 'true'
  } catch {
    return false
  }
})()
const sdbg = (...args: unknown[]) => { if (swapDebug) console.log('[VideoPlayer]', ...args) }

// The order the three quality words are offered in: cheapest first, the
// way the BBC lists them, so the tier a viewer reaches for when the
// connection is struggling is the one nearest the top of the menu.
const QUALITY_TIER_ORDER: QualityTier[] = ['low', 'medium', 'highest']

// How long the controls stay up after the last interaction.
// One skip distance for every input: the two buttons flanking play/pause and
// the arrow keys. The keys used to move 5s, which made the ±10 icons a lie.
const SKIP_SECONDS = 10

const CONTROLS_IDLE_MS = 3000
// How long an open menu waits, untouched, before closing itself.
const MENU_IDLE_MS = 3000

// Subtitle text-size options shown in the CC menu. The `normal` and `big`
// presets are stored in viewers' browsers, so only their labels follow
// Gavin's 17 Sep mock (small / medium / large).
const SUBTITLE_SIZE_OPTIONS: { preset: SubtitleSizePreset; label: string }[] = [
  { preset: 'small', label: 'Small' },
  { preset: 'normal', label: 'Medium' },
  { preset: 'big', label: 'Large' },
]

// Popup-menu geometry, used to keep the menus inside the player box on small
// containers.
const CC_MENU_WIDTH = 220
// The compass against the size its height rule gives. It began as the split
// halves' size; the single compass now takes it too, so going into split and
// back leaves the right-hand radar where it was (Gavin, 9 Oct).
const COMPASS_SCALE = 0.75
// One press of the up / down arrow keys.
const VOLUME_STEP = 0.1
const PANES: PaneIndex[] = [0, 1]
// Split screen's View button: a dropdown (22 Oct), or ?viewbutton=v4 / v3 eyes.
const VIEW_BUTTON = viewButtonVariant()
// The View menu's Layout rows.
const LAYOUT_LABELS: Record<SplitLayout, string> = {
  // Gavin, 3 Nov: Single, Split, Grid (were One view, Two views, All views).
  1: 'Single',
  2: 'Split',
  all: 'Grid',
}
// The View menu's three rows, top to bottom, with the camera preset each is.
const VIEW_ROWS = [
  ['above', 'Above', VerticalAlignBottomIcon, 'topDown'],
  ['normal', 'Normal', RadioButtonCheckedIcon, 'default'],
  ['below', 'Below', VerticalAlignTopIcon, 'bottomUp'],
] as const
// Progress bar + control row + padding: the vertical space a bottom-anchored
// menu can never use.
const CONTROLS_HEIGHT_ALLOWANCE = 96

export interface VideoPlayerProps {
  src: string
  type?: string
  // The programme title, shown top-left and fading with the control bar.
  // Series and episode are the player-facing title (decision 5); `name` is
  // the admin-facing identifier and is used only as the fallback line for a
  // video that predates the other two. All three optional — with none of
  // them the overlay renders nothing.
  series?: string
  episode?: string
  name?: string
  participants?: Participant[]
  subtitleUrl?: string
  subtitleOffset?: number
  speakerAttributions?: Record<string, string>
  rotationAdjustment?: number
  // The four-pane layout's framing, as stored on the video (`four_view`).
  // Frames every locked layout (all views), whatever the name says.
  // Missing parts take the defaults in splitLayout.ts.
  fourView?: { pane_aspect?: number; zoom?: number } | null
  // The layout to open in, where ?split= does not say. The admin's framing
  // preview opens in four.
  initialSplitLayout?: SplitLayout
  onCueChange?: (cueIndex: number | null, cues: SubtitleCue[]) => void
  onPlayStateChange?: (playing: boolean) => void
  compact?: boolean
  fillHeight?: boolean
  autoPlay?: boolean
  initialView?: 'normal' | 'flat'
  // Start position in seconds, applied by the core once the source reports a
  // duration (clamped into range).
  initialTime?: number
  // Sphere camera to restore, in degrees. Pitch is view elevation (0 = horizon,
  // negative looks down) and is clamped to the active preset's limits.
  initialCamera?: { yaw?: number; pitch?: number }
  // Where the camera points up or down when the video opens, in degrees
  // (0 = the horizon, negative looks down): the video's `start_tilt`, set in
  // the admin. initialCamera's pitch, from a shared link, wins over it.
  startTilt?: number
  // T18 (docs/plans/2026-08-13-verification-integration-closeout.md): the
  // Language flyout's source list. Tolerant of two shapes so every existing
  // caller keeps compiling and rendering unchanged:
  //  - a plain language code (what every caller passes today) — normalizes
  //    to a base track, which is the only thing a bare string could have
  //    meant before this prop grew a second shape;
  //  - a SubtitleTrackOption (from useSubtitles.ts's mergeSubtitleTracks) —
  //    carries `kind`, and for an addendum, `addedAt`/`anchored`, so the
  //    flyout can show it distinctly from a track sealed with the video.
  // A caller with no addenda to offer can keep passing string[] forever;
  // only a caller with addenda needs to switch to mergeSubtitleTracks(...).
  subtitleLanguages?: (string | SubtitleTrackOption)[]
  masterLanguage?: string
  // Controlled subtitle language: when provided, the CC menu reflects it and
  // reports changes via onLanguageChange (lets a parent sync its own control).
  language?: string
  onLanguageChange?: (lang: string | undefined) => void
  // URL of the compass radar background image (defaults to /img/radar.jpeg).
  radarImage?: string
  // Optional inline verification of the HLS media segments as they load
  // (docs/plans/2026-08-13-verification-integration-closeout.md, Decision 4).
  //
  // OPTIONAL IS THE POINT. Omitted — which is every existing caller — the
  // player behaves exactly as it did before this prop existed: nothing is
  // hooked, no bytes are read, no listener is added. See attachSegmentVerifier
  // and the interaction-counting test that pins it. Supply one only once
  // root.json has verified against the chain; the SegmentVerifier constructor
  // requires a `RootVerified` for exactly that reason.
  //
  // T15 only observes: results leave through the verifier's own onResult and
  // change nothing here. Halting on a terminal failure is T16's ticket.
  verifier?: SegmentSource
  // Set when verification has terminally failed — bytes arrived, were hashed,
  // and disagreed with what the publisher sealed on chain (Decision 4).
  //
  // Playback stops and stays stopped for the rest of the session. This is the
  // ONLY thing that halts the player: a fetch or decode failure is not a
  // mismatch and does not arrive here at all, it takes the existing
  // streamError path below, untouched.
  //
  // Owned by useVideoVerification, whose policy decides it once and never
  // clears it. The player renders and enforces that decision; it does not
  // make it, and there is nothing here that can un-make it.
  verificationFailure?: VerificationHalt | null
  // Two slots for the verification surface: the control-bar glyph, and the
  // modal it opens. Both are RENDER PROPS rather than nodes, because what they
  // draw has to follow the playhead and the buffer — see
  // PlayerVerificationContext — and the player is the only thing that holds
  // those. Omitted (every caller but the two public pages) nothing renders and
  // the layout is untouched.
  //
  // SLOTS RATHER THAN A PROP SET, because the player must not learn what
  // verification is: it renders whatever it is handed and knows nothing about
  // chain records, anchor states or countdowns.
  //
  // They live INSIDE the player rather than beside it because fullscreen
  // targets this component's own wrapper: anything floated over the player by
  // a page would vanish the moment a viewer went fullscreen, which is exactly
  // when they are most immersed in the bytes it vouches for.
  //
  // The control renders in the bottom control row, immediately after the
  // subtitles (CC) button.
  verificationControl?: (ctx: PlayerVerificationContext) => React.ReactNode
  // The overlay renders over the whole surface, above the controls (zIndex 12)
  // and OUTSIDE the idle fade — a viewer who stopped moving the mouse has not
  // stopped caring whether the video is what the publisher sealed. While it
  // returns anything, the controls stop auto-hiding too.
  verificationOverlay?: (ctx: PlayerVerificationContext) => React.ReactNode
}

/**
 * What the player hands its verification slots. Everything a chart needs to
 * place itself against the media, and nothing about verification itself.
 *
 * `getTime` and `getBufferedEnd` are REF-BACKED FUNCTIONS, not values, and
 * their identity is stable for the life of the mount. That is the whole point:
 * a chart's requestAnimationFrame loop reads the playhead 60 times a second,
 * and a `currentTime` value prop would re-render it on every `timeupdate`.
 * Calling either triggers no render at all.
 */
export interface PlayerVerificationContext {
  /** Seconds into the media, as of the last `timeupdate`. */
  getTime: () => number
  /** The end of the last buffered range, in seconds; 0 before any data has
   * loaded. Nothing beyond this has arrived, so nothing beyond it can have
   * been hashed. */
  getBufferedEnd: () => number
  duration: number
  paused: boolean
  isFullscreen: boolean
  /** The colour a bar button wears while its menu is open, from the skin, so
   * the verification glyph can wear it while its window is. */
  openIcon: string
}

export interface VideoPlayerHandle {
  seekTo: (time: number) => void
  getCurrentTime: () => number
  getDuration: () => number
}

const VideoPlayer = forwardRef<VideoPlayerHandle, VideoPlayerProps>((props, ref) => {
  const {
    src,
    type = 'application/x-mpegURL',
    series,
    episode,
    name,
    participants = [],
    subtitleUrl,
    subtitleOffset = 0,
    speakerAttributions,
    onCueChange,
    onPlayStateChange,
    compact,
    fillHeight,
    autoPlay,
    initialView,
    initialTime,
    initialCamera,
    startTilt = 0,
    rotationAdjustment,
    fourView,
    initialSplitLayout,
    subtitleLanguages = [],
    masterLanguage,
    language,
    onLanguageChange,
    radarImage,
    verifier,
    verificationFailure,
    verificationControl,
    verificationOverlay,
  } = props

  // One name for "this session is over", read by the play paths below and by
  // the overlay at the bottom of the render.
  const halted = Boolean(verificationFailure)

  const baseCaps = useMemo(() => detectCapabilities(readEnvironment()), [])
  const content = useMemo(
    () => ({ hasParticipants: participants.length > 0, hasSubtitles: !!subtitleUrl }),
    [participants.length, subtitleUrl],
  )
  const cachedTextureLiveRef = useRef<boolean | undefined>(undefined)
  const [textureLive, setTextureLive] = useState<boolean>(() => {
    const cached = getCachedTextureLive(Date.now(), navigator.userAgent, window.localStorage)
    cachedTextureLiveRef.current = cached
    return initialTextureLive(baseCaps, cached)
  })
  const caps = useMemo(() => ({ ...baseCaps, textureLive }), [baseCaps, textureLive])
  const [view, setView] = useState<PlayerView>(() => {
    const override = initialView ? (initialView === 'flat' ? 'flat' : 'sphere') : undefined
    return resolveFeatures(caps, content, override).defaultSurface === 'flat' ? 'flat' : (initialView || 'normal')
  })
  const kind = surfaceForView(view)
  const flags = resolveControls(kind, caps, content)
  // The view resolved above decides the core's first source, so it must be
  // settled before the core is constructed — it is: this runs during the first
  // render, the core is created in a mount effect.
  const initialSurfaceView = useRef(view).current

  // Shared player state
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [volume, setVolume] = useState(1)
  const [isMuted, setIsMuted] = useState(false)
  // What the slider shows: muted reads as silence, not as the level the
  // sound will come back at.
  const shownVolume = isMuted ? 0 : volume
  const [isBuffering, setIsBuffering] = useState(false)
  const [streamError, setStreamError] = useState<string | null>(null)
  // Real fullscreen (the Fullscreen API) or the stand-in below.
  const [nativeFullscreen, setNativeFullscreen] = useState(false)
  // iPhone has no Fullscreen API for anything but a bare <video> — and that
  // would hand the picture to iOS's own flat player, without the sphere,
  // radar or controls. So there the button pins the player over the whole
  // page instead (Gavin, 3 Nov: "expand screen button on iPhone doesn't
  // work"). Safari's own address bar cannot be hidden from a page; turning
  // the phone sideways shrinks it, and a home-screen bookmark has none.
  const [pseudoFullscreen, setPseudoFullscreen] = useState(false)
  const isFullscreen = nativeFullscreen || pseudoFullscreen
  const [seekingTime, setSeekingTime] = useState<number | null>(null)
  const [playbar] = useState(playbarVariant)
  const seekingTimeRef = useRef<number | null>(null)
  // The scrub bar's hover readout: the time under the pointer, and the pointer's
  // offset within the bar so the label can be drawn there. Mouse pointers only —
  // a touch has no hover, and a tap would otherwise strand the readout on screen
  // with nothing to move or dismiss it.
  const [scrubHover, setScrubHover] = useState<{ time: number, x: number } | null>(null)
  // Ref mirrors of the playhead and the buffer, for the verification slots.
  // They exist BECAUSE the state above cannot serve that reader: a chart
  // animating at 60fps must not re-render whenever `currentTime` moves, so it
  // reads these through the stable getters in `verificationContext` below.
  // `currentTimeRef` is written beside setCurrentTime in the core's
  // onTimeUpdate; `bufferedEndRef` is written from onProgress, which exists
  // only for this.
  const currentTimeRef = useRef(0)
  const bufferedEndRef = useRef(0)
  // The same buffer end as state, for the one reader that SHOULD re-render
  // with it: the scrub bar's loaded band. `progress` fires a few times a
  // second at most, which is nothing next to `timeupdate`.
  const [bufferedEnd, setBufferedEnd] = useState(0)
  // The control bar's real height, measured. Subtitles sit on top of it, and
  // a hardcoded offset went stale the moment the bar grew (56px buttons, the
  // scrub row above them) and left captions hidden behind it.
  const controlsOverlayRef = useRef<HTMLDivElement>(null)
  const [controlsHeight, setControlsHeight] = useState(0)
  useEffect(() => {
    const el = controlsOverlayRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const measure = () => setControlsHeight(Math.round(el.getBoundingClientRect().height))
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  // Sphere-only display state
  const [currentYaw, setCurrentYaw] = useState(0)
  const [currentPitch, setCurrentPitch] = useState(0)
  // Horizontal, not the camera's 75° vertical lens — see effectiveHorizontalFov
  // in the controller. 90 is where a 4:3 player sits at zoom 1; the first
  // camera update replaces it.
  const [currentFov, setCurrentFov] = useState(90)
  const [currentFovVertical, setCurrentFovVertical] = useState(75)
  const [currentViewPreset, setCurrentViewPreset] = useState<'default' | 'topDown' | 'bottomUp'>('default')
  const [qualityLevels, setQualityLevels] = useState<QualityLevelInfo[]>([])
  const renditionPinnedRef = useRef(false)
  const [activeQualityIndex, setActiveQualityIndex] = useState(-1)

  // UI controls state
  // The camera button's menu: the three camera views, Grid (Gavin, 17 Sep
  // mock — it replaces the View / Follow words that stood under the radar).
  const [showViewMenu, setShowViewMenu] = useState(false)
  const [showSettingsMenu, setShowSettingsMenu] = useState(false)
  const [showSubtitlesMenu, setShowSubtitlesMenu] = useState(false)
  // Fixed for the life of the mount — see skin.ts for why it is a query string.
  const [skin] = useState(skinFromQuery)
  const skinTokens = SKINS[skin]
  // uiHidden as of the last render, and whether the current tap began with
  // the UI hidden — see the idle timer and handleSurfacePointerUp.
  const uiHiddenRef = useRef(false)
  const tapWokeUiRef = useRef(false)
  const [selectedQuality, setSelectedQuality] = useState<'auto' | number>('auto')
  // Which of the three words is ticked. Held separately from
  // selectedQuality rather than derived from it: on a short ladder two
  // tiers can resolve to the same rung (see tierTargets), and deriving
  // would then tick both. Null means a raw rendition was pinned from the
  // debug ladder, which is no tier at all.
  const [selectedTier, setSelectedTier] = useState<QualityTier | null>('highest')
  const [showTranscript, setShowTranscript] = useState(false)
  const [subtitlesVisible, setSubtitlesVisible] = useState(true)
  // Follow the active speaker by default; the Follow button toggles it off.
  const [autoRotate, setAutoRotate] = useState(true)
  const [spatialAudio, setSpatialAudio] = useState(false)
  // Split screen: the sphere drawn through one to four cameras
  // (splitLayout.ts). ?split=2 or ?split=all starts in that layout; older
  // links' 1, 3 and 4 are read as two, all and all.
  const [splitLayout, setSplitLayout] = useState<SplitLayout>(() =>
    splitLayoutFromParam(new URLSearchParams(window.location.search).get('split')) ?? initialSplitLayout ?? 1,
  )
  const fourViewAspect = fourView?.pane_aspect
  const fourViewZoom = fourView?.zoom
  const fourViewFraming = useMemo(
    () => resolveFourView({ pane_aspect: fourViewAspect, zoom: fourViewZoom }),
    [fourViewAspect, fourViewZoom],
  )
  // All views is one locked pane per participant: nobody steers them, and
  // each faces one person, as the old grid video framed them.
  const paneLayout = resolvePaneLayout(splitLayout, participants.length)
  const locked = paneLayout.locked && kind === 'sphere'
  const positionsKey = participants.map((p) => p.position).join(',')
  const paneHeadings = useMemo(
    () => (paneLayout.locked ? lockedHeadings(paneLayout.count, positionsKey ? positionsKey.split(',').map(Number) : []) : []),
    [paneLayout.locked, paneLayout.count, positionsKey],
  )
  // All views only means something with two people or more.
  const layoutChoices = SPLIT_LAYOUTS.filter((n) => n !== 'all' || hasAllViews(participants.length) || splitLayout === 'all')
  // The two-pane layout's right half camera, for its own compass and View
  // menu. Follow starts off there: with both halves following, they would only
  // ever show the same speaker.
  const [splitCamera, setSplitCamera] = useState({ yaw: 0, fov: 90, fovVertical: 75 })
  const [splitViewPreset, setSplitViewPreset] = useState<'default' | 'topDown' | 'bottomUp'>('default')
  const [splitAutoRotate, setSplitAutoRotate] = useState(false)
  // Which half's View menu is open in split screen (0 left, 1 right).
  const [paneMenu, setPaneMenu] = useState<PaneIndex | null>(null)
  const paneMenuRefs = useRef<(HTMLDivElement | null)[]>([null, null])
  const lastSplitAutoRotatedSpeaker = useRef<string>('')
  // The playing video's intrinsic aspect ratio, once the pipeline reports it.
  // Read for ONE purpose: sizing the grid column so the transcript panel can
  // take the strip beside it (see gridSidebarWidth). Null until metadata
  // arrives, and re-read on `resize`.
  const [videoAspect, setVideoAspect] = useState<number | null>(null)

  // Subtitle language selection. Controlled when `language` is provided
  // (a parent owns the choice); otherwise the player owns it internally.
  const [internalLang, setInternalLang] = useState<string | undefined>(masterLanguage)
  const selectedLang = language !== undefined ? language : internalLang
  const changeLang = (l: string | undefined) => {
    setInternalLang(l)
    onLanguageChange?.(l)
  }

  // Gyro availability and state — surfaced from Sphere360Surface
  const [gyroAvailable, setGyroAvailable] = useState(false)
  const [isGyroEnabled, setIsGyroEnabled] = useState(false)

  // Idle UI fade: overlays fade out after 10s without interaction while
  // playing; any pointer/key activity brings them back. Menus and pause
  // always keep the UI visible.
  const [isIdle, setIsIdle] = useState(false)
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Subtitle size preference — Small / Normal / Large, persisted so viewers keep
  // their choice. The actual px size is this preset times the container-derived
  // base, so the same preset reads sensibly on a phone and on a desktop.
  const [subtitleSizePreset, setSubtitleSizePreset] = useState<SubtitleSizePreset>(() => {
    try {
      const stored = window.localStorage.getItem('forum-subtitle-size')
      if (stored === 'small' || stored === 'normal' || stored === 'big') return stored
      // One-time migration from the old percentage stepper. The old key is only
      // ever read, never written.
      const legacy = parseFloat(window.localStorage.getItem('forum-subtitle-scale') || '')
      if (Number.isFinite(legacy)) return migrateSubtitleScale(legacy)
      return 'normal'
    } catch {
      return 'normal'
    }
  })
  const changeSubtitleSizePreset = (next: SubtitleSizePreset) => {
    setSubtitleSizePreset(next)
    try { window.localStorage.setItem('forum-subtitle-size', next) } catch { /* ignore */ }
  }

  // Auto-rotate tracking
  const lastAutoRotatedSpeaker = useRef<string>('')

  // Track last 360° view for the 360 button
  const lastSphereViewRef = useRef<PlayerView>('normal')

  // Menu wrappers — used to dismiss popups on outside click.
  const subtitlesMenuRef = useRef<HTMLDivElement>(null)
  const settingsMenuWrapRef = useRef<HTMLDivElement>(null)
  const viewMenuRef = useRef<HTMLDivElement>(null)

  const sphereRef = useRef<Sphere360Handle | null>(null)
  // Stable, so the ?zoominfo=1 readout's polling interval is set up once.
  const readZoomDiagnostics = useCallback(() => sphereRef.current?.getZoomDiagnostics() ?? null, [])
  const containerRef = useRef<HTMLDivElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  // The persistent wrapper the core's video element lives in. It is mounted in
  // every view — hidden while the sphere renders it as a texture, laid out by
  // FlatSurface in the flat view — so the element never moves in the DOM.
  const videoHostRef = useRef<HTMLDivElement>(null)
  const probedRef = useRef(false)
  // The camera to restore the next time a sphere mounts: the URL's on the first
  // mount, then whatever the user was last looking at, so returning from the
  // flat view doesn't snap back to North.
  // The video's starting tilt fills in a pitch the URL did not give.
  const cameraRef = useRef<{ yaw?: number; pitch?: number } | undefined>(
    initialCamera && Number.isFinite(initialCamera.pitch)
      ? initialCamera
      : startTilt ? { yaw: initialCamera?.yaw, pitch: startTilt } : initialCamera
  )
  // A tilt that changes while mounted (the admin's slider) moves the camera
  // there, keeping its heading. The first run is the mount, handled above.
  const appliedTiltRef = useRef(startTilt)
  useEffect(() => {
    if (appliedTiltRef.current === startTilt) return
    appliedTiltRef.current = startTilt
    sphereRef.current?.setCamera(cameraRef.current?.yaw ?? 0, startTilt)
  }, [startTilt])

  // Container-driven layout: the wrapper decides orientation / transcript mode
  // / sidebar width, the video column decides subtitle size, narrow controls
  // and the height the compass scales against.
  const layout = usePlayerLayout(wrapperRef, containerRef)

  // Final subtitle px size: container-derived base scaled by the user preset.
  const subtitleFontSize = layout.subtitleBase * SUBTITLE_SIZE_PRESETS[subtitleSizePreset]

  // The one media pipeline this mount ever has. Kept in a ref for callbacks and
  // in state so the surfaces can render against it.
  const coreRef = useRef<PlaybackCore | null>(null)
  const [core, setCore] = useState<PlaybackCore | null>(null)
  // Everything the core is born with, frozen at mount. A later prop change
  // reaches it through setSource (below), never through a second construction.
  const bootRef = useRef({ src, type, initialTime, autoPlay })

  // Build the pipeline once per mount, in an effect so StrictMode's double
  // invoke disposes the first one, and tear it down only on unmount. Every
  // media event the UI renders arrives through these callbacks — they touch
  // only setState and refs, so freezing them here can't go stale.
  useEffect(() => {
    const host = videoHostRef.current
    if (!host) return
    const boot = bootRef.current
    sdbg('creating core', { view: initialSurfaceView, ...boot })
    const created = new PlaybackCore(
      host,
      boot.src,
      boot.type,
      { initialTime: boot.initialTime, autoPlay: boot.autoPlay },
      {
        onPlayStateChange: setIsPlaying,
        onTimeUpdate: (t: number) => {
          currentTimeRef.current = t
          setCurrentTime(t)
          // Clear seeking state once we arrive close enough to the target
          if (seekingTimeRef.current !== null && Math.abs(t - seekingTimeRef.current) < 0.3) {
            setSeekingTime(null)
            seekingTimeRef.current = null
          }
        },
        onProgress: (bufferedEnd: number) => {
          bufferedEndRef.current = bufferedEnd
          setBufferedEnd(bufferedEnd)
        },
        onDurationChange: setDuration,
        onVolumeChange: (v: number, m: boolean) => { setVolume(v); setIsMuted(m) },
        onBufferingChange: setIsBuffering,
        onError: setStreamError,
        onQualityLevelsChange: (levels: QualityLevelInfo[], activeIdx: number) => {
          // A source change empties the ladder until the new manifest is
          // parsed. Keep the last one rendered so the menu doesn't blink out
          // mid-switch, but the controller has dropped any manual pin, so the
          // readout goes back to auto until real levels arrive.
          if (levels.length === 0) {
            setActiveQualityIndex(-1)
            setSelectedQuality('auto')
            setSelectedTier('highest')
            return
          }
          setQualityLevels(levels)
          setActiveQualityIndex(activeIdx)
          // ?rendition=1440p (QualityReadout.tsx): pin one rung as soon as the
          // ladder is known, for testing without the debug menu, which a
          // phone held sideways is too short to show.
          if (pinnedRendition && !renditionPinnedRef.current) {
            const level = levels.find((l) => qualityLabel(l).toLowerCase() === pinnedRendition)
            if (level) {
              renditionPinnedRef.current = true
              setSelectedTier(null)
              setSelectedQuality(level.index)
              // Next tick: this callback can run inside the core's constructor.
              setTimeout(() => coreRef.current?.setQualityLevel(level.index), 0)
            }
          }
        },
      },
    )
    coreRef.current = created
    setCore(created)
    return () => {
      created.dispose()
      coreRef.current = null
      setCore(null)
    }
  }, [initialSurfaceView])

  // The only way the source ever changes: the src props themselves changing.
  // Every view plays the one 360° stream. setSource is a no-op when the URL is
  // unchanged, so this is inert on mount.
  useEffect(() => {
    coreRef.current?.setSource(src, type)
  }, [core, src, type])

  // Segment verification, when a caller asked for it. Bound to the core's
  // player rather than to a source, because the player outlives every view
  // crossing and VHS re-announces its loader (`xhr-hooks-ready`) on each new
  // source — the verifier follows that itself.
  //
  // With no verifier, attachSegmentVerifier returns without reading `player`
  // at all, so this effect is inert for every existing caller.
  useEffect(() => {
    if (!core) return
    return attachSegmentVerifier(verifier, core.player as unknown as VerifiablePlayer)
  }, [core, verifier])

  // Enforce the halt. Keyed on isPlaying as well as the failure so that
  // anything which resumes playback afterwards — an autoplay retry, a stray
  // tap, a browser media key — is put straight back to a stop. Pausing is
  // idempotent and the failure never clears, so this settles immediately
  // rather than fighting anything: it is not a retry loop, it is a floor.
  useEffect(() => {
    if (!verificationFailure) return
    if (!isPlaying) return
    coreRef.current?.pause()
  }, [verificationFailure, isPlaying])

  // The scrim's own fade. Mounted transparent and flipped visible one frame
  // later, because a browser has nothing to ease from if an element appears
  // already at its final opacity. Under reduced motion the style carries no
  // transition, so the identical flip lands as a cut — see failureScrim.ts.
  //
  // It never flips back: `verificationFailure` is set once and never cleared
  // (useVideoVerification owns that), so this is a floor, not a toggle.
  const [scrimVisible, setScrimVisible] = useState(false)
  useEffect(() => {
    if (!halted) return undefined
    const frame = window.requestAnimationFrame(() => setScrimVisible(true))
    return () => window.cancelAnimationFrame(frame)
  }, [halted])

  const effectiveSubtitleUrl = useMemo(() => {
    if (!subtitleUrl) return undefined
    if (!selectedLang || selectedLang === masterLanguage) return subtitleUrl
    const sep = subtitleUrl.includes('?') ? '&' : '?'
    return `${subtitleUrl}${sep}lang=${selectedLang}`
  }, [subtitleUrl, selectedLang, masterLanguage])

  const { currentCue, currentCueIndex, cues } = useSubtitles(effectiveSubtitleUrl, currentTime, speakerAttributions, subtitleOffset)

  // T18: normalize `subtitleLanguages` to SubtitleTrackOption[] once, so the
  // rest of the render never branches on which shape a caller passed. See
  // normalizeSubtitleTracks's doc comment — this is the regression guard for
  // every pre-T18 caller.
  const subtitleTracks: SubtitleTrackOption[] = useMemo(
    () => normalizeSubtitleTracks(subtitleLanguages),
    [subtitleLanguages],
  )
  const normalizedMasterLanguage = masterLanguage?.trim().toLowerCase()
  // D11: compared on the same normalized form the tracks themselves use, so
  // a mismatched-case `language`/`masterLanguage` prop can't fail to find
  // its own track.
  const normalizedSelectedLang = (selectedLang || masterLanguage)?.trim().toLowerCase()
  const activeSubtitleTrack = subtitleTracks.find((t) => t.lang === normalizedSelectedLang) ?? null

  // Arm the idle timer: any interaction shows the UI and restarts the
  // countdown. The timer only sets the idle flag — whether the UI actually
  // hides also depends on playback/menus (see uiHidden below).
  useEffect(() => {
    const el = wrapperRef.current
    if (!el) return
    const wake = (e?: Event) => {
      // Was the UI faded out when this touch began? Read from a ref, now,
      // because by the time the surface's own pointerup runs the wake has
      // already brought the controls back.
      // Touch and pen only: a mouse has hover, so its controls are already up
      // before it clicks, and a click should pause at once.
      if (e?.type === 'pointerdown') tapWokeUiRef.current = (e as PointerEvent).pointerType !== 'mouse' && uiHiddenRef.current
      setIsIdle(false)
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current)
      idleTimerRef.current = setTimeout(() => setIsIdle(true), CONTROLS_IDLE_MS)
    }
    wake()
    el.addEventListener('pointermove', wake)
    el.addEventListener('pointerdown', wake)
    el.addEventListener('touchstart', wake, { passive: true })
    window.addEventListener('keydown', wake)
    return () => {
      el.removeEventListener('pointermove', wake)
      el.removeEventListener('pointerdown', wake)
      el.removeEventListener('touchstart', wake)
      window.removeEventListener('keydown', wake)
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current)
    }
  }, [])

  const closeAllMenus = () => {
    setShowSubtitlesMenu(false)
    setShowSettingsMenu(false)
    setShowViewMenu(false)
    setPaneMenu(null)
  }

  // Dismiss the popup menus when clicking anywhere outside them.
  useEffect(() => {
    if (!showSubtitlesMenu && !showSettingsMenu && !showViewMenu && paneMenu === null) return
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (paneMenu !== null && !paneMenuRefs.current[paneMenu]?.contains(target)) {
        setPaneMenu(null)
      }
      if (viewMenuRef.current && !viewMenuRef.current.contains(target)) {
        setShowViewMenu(false)
      }
      if (subtitlesMenuRef.current && !subtitlesMenuRef.current.contains(target)) {
        setShowSubtitlesMenu(false)
      }
      if (settingsMenuWrapRef.current && !settingsMenuWrapRef.current.contains(target)) {
        setShowSettingsMenu(false)
      }
    }
    // pointerdown, not mousedown: on a phone the compatibility mousedown
    // comes after the whole tap, or not at all on a non-clickable target.
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [showSubtitlesMenu, showSettingsMenu, showViewMenu, paneMenu])

  // An open menu closes itself after MENU_IDLE_MS without a touch, click, key
  // or scroll (Gavin, 3 Nov: "the dropdowns need to disappear after 3
  // seconds", whether or not anything was chosen). Hovering does not count —
  // on a phone there is no hover, and on a desktop a resting mouse would
  // otherwise hold a menu open for ever.
  const anyMenuOpen = showSubtitlesMenu || showSettingsMenu || showViewMenu || paneMenu !== null
  useEffect(() => {
    if (!anyMenuOpen) return
    const el = wrapperRef.current
    let timer = setTimeout(closeAllMenus, MENU_IDLE_MS)
    const restart = () => {
      clearTimeout(timer)
      timer = setTimeout(closeAllMenus, MENU_IDLE_MS)
    }
    const events = ['pointerdown', 'wheel', 'touchmove', 'keydown'] as const
    for (const name of events) el?.addEventListener(name, restart, { passive: true })
    return () => {
      clearTimeout(timer)
      for (const name of events) el?.removeEventListener(name, restart)
    }
  }, [anyMenuOpen])

  useImperativeHandle(ref, () => ({
    seekTo: (t: number) => coreRef.current?.seek(t),
    getCurrentTime: () => currentTime,
    getDuration: () => duration,
  }), [currentTime, duration])

  useEffect(() => { onCueChange?.(currentCueIndex, cues) }, [currentCueIndex, cues, onCueChange])
  useEffect(() => { onPlayStateChange?.(isPlaying) }, [isPlaying, onPlayStateChange])

  // Fullscreen state tracking
  useEffect(() => {
    const doc = document as Document & { webkitFullscreenElement?: Element }
    const handleFullscreenChange = () => {
      const isFull = !!(document.fullscreenElement || doc.webkitFullscreenElement)
      setNativeFullscreen(isFull)
    }
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange)
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange)
    }
  }, [])

  // The stand-in fullscreen: no page scroll behind it, and Escape leaves it
  // as it leaves the real thing.
  useEffect(() => {
    if (!pseudoFullscreen) return
    const { overflow } = document.body.style
    document.body.style.overflow = 'hidden'
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setPseudoFullscreen(false) }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = overflow
      window.removeEventListener('keydown', onKey)
    }
  }, [pseudoFullscreen])

  // After a fullscreen transition, nudge a resize so the sphere renderer
  // re-measures its (now full-screen or restored) container.
  useEffect(() => {
    const t = setTimeout(() => window.dispatchEvent(new Event('resize')), 60)
    return () => clearTimeout(t)
  }, [isFullscreen])

  // Track the video's intrinsic aspect ratio. Same event set FlatSurface lays
  // out on, and for the same reason: `loadedmetadata` alone is not enough,
  // because Safari's native HLS pipeline fires it while videoWidth is still 0.
  useEffect(() => {
    if (!core) return
    const el = core.videoElement
    const read = () => {
      const w = el.videoWidth
      const h = el.videoHeight
      if (w > 0 && h > 0) setVideoAspect(w / h)
    }
    const events = ['loadedmetadata', 'resize', 'loadeddata', 'canplay'] as const
    for (const name of events) el.addEventListener(name, read)
    read()
    return () => { for (const name of events) el.removeEventListener(name, read) }
  }, [core])

  // Seek by a signed number of seconds, clamped to the media. Shared by the
  // skip buttons and the arrow keys so a click and a key press always move the
  // playhead by the same distance.
  const skipBy = useCallback((seconds: number) => {
    const target = seconds >= 0
      ? Math.min(currentTime + seconds, duration)
      : Math.max(currentTime + seconds, 0)
    coreRef.current?.seek(target)
  }, [currentTime, duration])

  // Keyboard controls
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore shortcuts while typing in an input/textarea/contenteditable
      // (e.g. the transcript editor) so Space/Arrows reach the field.
      const target = e.target as HTMLElement | null
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return
      }
      if (e.code === 'Space' || e.key === ' ') {
        e.preventDefault()
        // Halted: pause still works, play does not. Same rule at every play
        // site below — the stop is terminal, so nothing offers a way past it.
        if (isPlaying) {
          coreRef.current?.pause()
        } else if (!halted) {
          coreRef.current?.play()
        }
      }
      if (e.code === 'ArrowRight' || e.key === 'ArrowRight') {
        e.preventDefault()
        skipBy(SKIP_SECONDS)
      }
      if (e.code === 'ArrowLeft' || e.key === 'ArrowLeft') {
        e.preventDefault()
        skipBy(-SKIP_SECONDS)
      }
      // Up / down: volume, a tenth at a time. Up from mute starts from
      // silence, which is where the slider stands while muted.
      if (e.code === 'ArrowUp' || e.key === 'ArrowUp') {
        e.preventDefault()
        coreRef.current?.setVolume(Math.min(1, Math.round((shownVolume + VOLUME_STEP) * 100) / 100))
      }
      if (e.code === 'ArrowDown' || e.key === 'ArrowDown') {
        e.preventDefault()
        coreRef.current?.setVolume(Math.max(0, Math.round((shownVolume - VOLUME_STEP) * 100) / 100))
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isPlaying, halted, skipBy, shownVolume])

  // Re-apply the 3D-audio enabled state whenever a sphere surface (re)mounts —
  // a freshly created controller starts disabled, so without this the toggle
  // would read "on" after a view switch while audio stayed un-spatialized.
  useEffect(() => {
    // Single view only. Gavin, 3 Nov: "On grid and split lose 3D audio". The
    // panner follows the main camera alone, so with two or more views on
    // screen the sound turned to face whoever the first pane happened to be
    // pointing at. The viewer's choice is kept, and comes back with Single.
    if (kind !== 'sphere' || splitLayout !== 1) {
      // Nothing feeds the panner a listener orientation without a sphere, so
      // leaving it enabled would pin flat-view audio to a stale heading. The
      // manager itself stays alive in the core (createMediaElementSource can
      // only ever run once per element) — this just bypasses it.
      coreRef.current?.spatialAudio.setEnabled(false)
      return
    }
    sphereRef.current?.setSpatialAudioEnabled(spatialAudio)
  }, [kind, spatialAudio, core, splitLayout])

  // Verify the optimistically-mounted sphere actually renders live pixels.
  // The black-texture failure is silent (video plays, audio plays, sphere is
  // black), so user-agent detection can't catch it — only this runtime probe.
  useEffect(() => {
    if (!isPlaying || kind !== 'sphere' || probedRef.current) return
    if (!shouldVerifySphere(baseCaps, cachedTextureLiveRef.current)) return
    // ?probe=0 — never downgrade to flat, so a probe that misjudges a working
    // sphere can be told apart from a sphere that is really black.
    if (new URLSearchParams(window.location.search).get('probe') === '0') return
    const handle = sphereRef.current
    if (!handle) return
    probedRef.current = true
    let cancelled = false
    probeTexture(webglProbeDeps(handle)).then((live) => {
      if (cancelled) return
      setCachedTextureLive(live, Date.now(), navigator.userAgent, window.localStorage)
      if (!live) {
        setTextureLive(false)
        changeView('flat')
      }
    })
    return () => { cancelled = true }
  }, [isPlaying, kind])

  // Update spatial audio with active speaker position — sphere only
  useEffect(() => {
    if (kind !== 'sphere') return
    const sphere = sphereRef.current
    if (!spatialAudio || !currentCue?.speaker || !participants.length) {
      sphere?.setActiveSpeaker(null)
      return
    }
    const participant = participants.find((p) => p.id === currentCue.speaker)
    if (participant) {
      sphere?.setActiveSpeaker(participant.position)
    } else {
      sphere?.setActiveSpeaker(null)
    }
  }, [currentCue, spatialAudio, participants, kind])

  // Auto-rotate to active speaker — sphere only
  useEffect(() => {
    if (kind !== 'sphere' || locked) return
    if (!autoRotate || !currentCue?.speaker || !participants.length) return
    if (currentCue.speaker === lastAutoRotatedSpeaker.current) return
    const participant = participants.find((p) => p.id === currentCue.speaker)
    if (participant) {
      sphereRef.current?.rotateTo((360 - participant.position) % 360, false)
      lastAutoRotatedSpeaker.current = currentCue.speaker
    }
  }, [currentCue, autoRotate, participants, kind, locked])

  // The same for the right half in split screen, on its own Follow switch.
  useEffect(() => {
    if (kind !== 'sphere' || splitLayout !== 2) return
    if (!splitAutoRotate || !currentCue?.speaker || !participants.length) return
    if (currentCue.speaker === lastSplitAutoRotatedSpeaker.current) return
    const participant = participants.find((p) => p.id === currentCue.speaker)
    if (participant) {
      sphereRef.current?.rotateTo((360 - participant.position) % 360, false, 1)
      lastSplitAutoRotatedSpeaker.current = currentCue.speaker
    }
  }, [currentCue, splitAutoRotate, splitLayout, participants, kind])

  // Flat-view fullscreen — toggles on the shell wrapper div using DOM API with webkit fallback
  // Fullscreen targets the shell wrapper (which contains the surface AND the
  // controls/compass/cluster) for BOTH surfaces, so the control bar stays
  // visible. The sphere renderer resizes to its container on the window
  // 'resize' event that firing fullscreen triggers.
  const toggleFullscreen = () => {
    const el = wrapperRef.current
    if (!el) return
    const doc = document as Document & { webkitFullscreenElement?: Element; webkitFullscreenEnabled?: boolean }
    if (pseudoFullscreen) {
      setPseudoFullscreen(false)
    } else if (document.fullscreenElement || doc.webkitFullscreenElement) {
      document.exitFullscreen?.()
      ;(document as any).webkitExitFullscreen?.()
    } else if (!(document.fullscreenEnabled || doc.webkitFullscreenEnabled)) {
      setPseudoFullscreen(true)
    } else {
      el.requestFullscreen?.()
      ;(el as any).webkitRequestFullscreen?.()
    }
  }

  // Change view. Crossing the sphere<->flat boundary swaps the presentation
  // layer only: the source change goes to the persistent core (see the
  // setSource effect above), so position, volume and the media pipeline itself
  // all survive the switch.
  const chooseLayout = (next: SplitLayout) => {
    setShowViewMenu(false)
    setPaneMenu(null)
    // Out of a locked layout the main camera is free again: let Follow turn it
    // to whoever is talking now, not wait for the next speaker change.
    if (splitLayout === 'all' && next !== 'all') lastAutoRotatedSpeaker.current = ''
    setSplitLayout(next)
  }

  const changeView = (next: PlayerView) => {
    // Same view again (e.g. probe + context-loss both downgrading): nothing to
    // do — and the non-crossing branch below would call setViewPreset with no
    // sphere mounted.
    if (next === view) {
      setShowViewMenu(false)
      return
    }
    const crossing = surfaceForView(next) !== surfaceForView(view)
    // The flat view is a way out of split: the 360 button brings back the one
    // normal picture, not the panes that were left behind (Gavin, 10 Oct).
    if (next === 'flat') setSplitLayout(1)
    sdbg('changeView', { from: view, to: next, crossing, isPlaying })
    if (crossing) {
      // Drop the outgoing source's error; the core clears it again on the next
      // successful load, but a stale "Stream failed to load" must not sit over
      // the new view in the meantime.
      setStreamError(null)
      // The incoming sphere is a freshly built controller; clear the follow
      // marker so auto-rotate re-applies to the active speaker on it.
      if (surfaceForView(next) === 'sphere') {
        lastAutoRotatedSpeaker.current = ''
      }
    } else {
      // Within sphere views (normal/above/below): just update the preset.
      sphereRef.current?.setViewPreset(
        next === 'above' ? 'topDown' : next === 'below' ? 'bottomUp' : 'default',
      )
    }
    // Track last sphere view so the 360 button can return to it
    if (surfaceForView(next) === 'sphere') {
      lastSphereViewRef.current = next
    }
    setView(next)
    setShowViewMenu(false)
  }

  // Manual escape hatch from a texture-probe downgrade: clear the cached
  // negative and bring the sphere back. The probe re-verifies on the fresh
  // surface (probedRef reset), so a genuinely dead texture downgrades again.
  const retry360 = () => {
    clearCachedTextureLive(window.localStorage)
    cachedTextureLiveRef.current = undefined
    probedRef.current = false
    setTextureLive(true)
    changeView(lastSphereViewRef.current)
  }

  // The transcript panel takes real layout space (a flex column on the
  // left); the video area is the remainder. The flat surface re-centers
  // via its shortest-dimension layout, the sphere re-measures via its
  // container ResizeObserver — the same calculation decides where the video
  // goes in both views.
  const transcriptOpen = showTranscript && cues.length > 0
  // Only the sidebar variant takes real layout space. In portrait containers
  // the transcript renders as a full-player overlay instead ("podcast mode"),
  // so none of the side-by-side layout (flex row, aspect pin, letterboxing)
  // applies. The mode follows the wrapper's shape live, so rotating a phone
  // converts one into the other without closing the transcript.
  const sidebarOpen = transcriptOpen && layout.transcriptMode === 'sidebar'
  const overlayOpen = transcriptOpen && layout.transcriptMode === 'overlay'

  // What the verification slots are handed. The two getters are stable for the
  // life of the mount (see PlayerVerificationContext); the three values change
  // rarely enough to pass as values.
  // Reads the player DIRECTLY, not the ref, because the ref is only written on
  // `timeupdate` — which fires about four times a second. A chart animating at
  // 60fps from a value that changes 4 times a second steps visibly; reading the
  // element gives it a true position on every frame. The ref stays as the
  // fallback for the window between mount and the core existing, and for after
  // teardown, where `currentTime()` would throw.
  const getTime = useCallback(() => {
    try {
      const live = coreRef.current?.getCurrentTime()
      if (typeof live === 'number' && Number.isFinite(live)) return live
    } catch {
      // Torn down mid-frame. The ref's last value is the honest answer.
    }
    return currentTimeRef.current
  }, [])
  const getBufferedEnd = useCallback(() => bufferedEndRef.current, [])
  const verificationContext = useMemo<PlayerVerificationContext>(
    () => ({ getTime, getBufferedEnd, duration, paused: !isPlaying, isFullscreen, openIcon: skinTokens.openIcon }),
    [getTime, getBufferedEnd, duration, isPlaying, isFullscreen, skinTokens.openIcon],
  )
  // Called here rather than at each render site so the overlay's emptiness is
  // one known fact: it decides both whether the overlay layer exists at all
  // and whether the controls are pinned below.
  const verificationControlNode = verificationControl ? verificationControl(verificationContext) : null
  const verificationOverlayNode = verificationOverlay ? verificationOverlay(verificationContext) : null

  // Hide overlay UI only while actually playing with no menu open. Podcast
  // mode pins the controls: they are the only playback affordance left once
  // the transcript covers the video. An open verification overlay pins them
  // too — the glyph that closes it lives in that row.
  const uiHidden =
    isIdle && isPlaying && !showViewMenu && !showSettingsMenu && !showSubtitlesMenu
    && paneMenu === null
    && !overlayOpen
    && !verificationOverlayNode
  uiHiddenRef.current = uiHidden
  const uiFadeStyle: React.CSSProperties = {
    opacity: uiHidden ? 0 : 1,
    pointerEvents: uiHidden ? 'none' : 'auto',
    transition: 'opacity 0.5s ease',
  }

  // Both ends of the scrub row share one style, so they stay the same size as
  // the column narrows and the bar between them keeps its full length.
  const timeLabelStyle: React.CSSProperties = {
    ...styles.timeLabel,
    ...(layout.isNarrowControls ? styles.timeLabelNarrow : {}),
  }

  // The transcript panel's language picker. The same list, in the same order
  // and with the same tags, as the CC menu's Language flyout below, driving
  // the same `selectedLang` (decision 1: transcript and subtitles are two views
  // on ONE language setting), so a change made in either place shows in both.
  const transcriptLanguages = useMemo<TranscriptLanguage[]>(() => {
    if (subtitleTracks.length === 0 || !normalizedMasterLanguage) return []
    return [
      { code: normalizedMasterLanguage, label: `${languageName(masterLanguage)} (Original)` },
      ...subtitleTracks
        .filter((t) => t.lang !== normalizedMasterLanguage)
        .map((t) => ({
          code: t.lang,
          label: t.kind === 'addendum'
            ? `${t.label} (${t.anchored && t.addedAt ? `added ${formatAddedDate(t.addedAt)}` : 'not yet on chain'})`
            : t.label,
        })),
    ]
  }, [subtitleTracks, masterLanguage, normalizedMasterLanguage])
  // The CC menu's language list is only worth showing when there is a choice
  // to make. One choice drives both the subtitles and the transcript.
  const showLanguageChoice = transcriptLanguages.length > 1

  // The camera button: everything that used to stand under the radar as words.
  const showFollow = flags.follow && participants.length > 0
  const showGyro = flags.gyro && gyroAvailable
  const showViewButton = flags.viewPresets || showGyro
  // In split screen each half gets a compass of its own, with that half's
  // camera button under it.
  const splitActive = splitLayout === 2 && kind === 'sphere'
  const showFollowButton = showFollow && !splitActive && !locked

  // What the control row gives up to fit the column — slider, then button
  // size, then the midline, then skip ±10. The counts mirror the render
  // conditions in the row below; see fitControlRow for the order and why.
  const rowFit = fitControlRow(layout.columnW, {
    leftButtons: 1 + (flags.spatialAudio && participants.length > 0 && splitLayout === 1 ? 1 : 0),
    rightButtons:
      (verificationControlNode ? 1 : 0)
      + (showFollowButton ? 1 : 0)
      + (showViewButton ? 1 : 0)
      + (subtitleUrl ? 1 : 0)
      + 1 // settings
      + (flags.switchTo360 ? 1 : 0)
      + 1, // fullscreen
  })
  const controlButton: React.CSSProperties = {
    ...styles.button,
    minWidth: `${rowFit.buttonSize}px`,
    minHeight: `${rowFit.buttonSize}px`,
  }
  // Unmirrored, the side zones size to their content and the centre sits
  // between them instead of on the midline. The row then spreads the three
  // zones to its edges: packed from the left instead, a phone on its side ran
  // every button into one undivided strip with the right of the bar empty
  // (Gavin, 16 Oct, iPhone) — the groups only read as groups with space
  // between them.
  const sideZone: React.CSSProperties = rowFit.mirrored ? {} : { flex: '0 0 auto' }
  const rowSpread: React.CSSProperties = rowFit.mirrored ? {} : { justifyContent: 'space-between' }

  // The compass, fitted into the height above the controls. Nothing stacks
  // under it any more — View / Follow moved into the camera button's menu.
  const clusterFit = fitTopRightCluster(
    layout.columnH,
    controlsHeight,
    Math.max(MIN_COMPASS, Math.round(compassSizeFor(layout.columnH) * COMPASS_SCALE)),
    splitActive ? 1 : 0,
  )

  // Popup paint, from the skin. Every menu is anchored to its button's right
  // edge; the settings menu keeps its own width and hint styles on top.
  const menuSlab: React.CSSProperties = {
    ...styles.qualityMenu,
    left: 'auto',
    right: 0,
    transform: 'none',
    // Blue-green's first band sits flush with the slab's top edge, as drawn.
    padding: skin === 'bluegreen' ? '0 0 6px' : '6px 0',
    backgroundColor: undefined,
    background: skinTokens.menuBackground,
    borderRadius: skinTokens.menuRadius,
    overflowY: 'auto',
  }
  const menuRow = (active: boolean): React.CSSProperties => ({
    ...styles.subtitleMenuRow,
    fontSize: skinTokens.rowFontSize,
    ...(active ? { backgroundColor: skinTokens.activeRow } : {}),
  })
  // An on/off row heading a menu — Follow, Transcript, Subtitles. While ON it
  // sits in the same band as a selected row (Gavin, 30 Sep: "can we highlight
  // when something is on like Large and German? Highlight Subtitles").
  const menuHeadingRow = (on: boolean): React.CSSProperties => ({
    ...styles.subtitleMenuRow,
    fontSize: skinTokens.rowFontSize,
    fontWeight: 700,
    backgroundColor: on ? skinTokens.activeRow : skinTokens.headingRow,
  })
  const menuSectionLabel: React.CSSProperties = skin === 'bluegreen'
    // The mock labels its sections as plain rows ("View"), not small caps.
    ? { ...styles.subtitleMenuSectionLabel, textTransform: 'none', letterSpacing: 0, opacity: 1, fontSize: skinTokens.rowFontSize, padding: '10px 16px 4px' }
    : styles.subtitleMenuSectionLabel
  // The camera menus' View and Layout headings, in the green of a lit bar
  // button (Gavin, 3 Nov) — in the bar's menu and in each split pane's;
  // the subtitle menu's Text size and Language too, the same day.
  const sectionHeading: React.CSSProperties = { ...menuSectionLabel, opacity: 1, color: skinTokens.openIcon }
  const menuIcon: React.CSSProperties = { fontSize: '18px', marginRight: 10, verticalAlign: 'middle' }
  // A selected row's tick, in the same green (Gavin, 3 Nov: "make ticks, when
  // selected, green too") — every menu's, not just the subtitle menu's.
  const tick: React.CSSProperties = { color: skinTokens.openIcon }
  // A bar button whose menu is open wears the skin's accent — the one button
  // that is green at a time. The verification glyph does the same for its
  // window, through `PlayerVerificationContext.openIcon`.
  const openButton = (open: boolean): React.CSSProperties => (open ? { color: skinTokens.openIcon } : {})
  // Tooltips are portalled into the wrapper, not document.body: the wrapper
  // is the element that goes fullscreen, and nothing outside it is drawn then.
  const tooltipSlots = useMemo(() => ({ popper: { container: () => wrapperRef.current } }), [])

  // The scrub bar, graded green to blue to match the radar compass. Pure CSS:
  // three stacked backgrounds on the range input, no image.
  //
  //   'fill'  (default) — played: the gradient; loaded but not yet played: a
  //           light band; not loaded: dim. The gradient always spans the whole
  //           bar, so the colour under the playhead also says how far in you are.
  //   'track' — the whole bar in the gradient, exactly as Gavin's swatch. Kept
  //           as a switch because it is the literal reading of the swatch, but
  //           it says nothing about progress or loading.
  const playbarGradient = `linear-gradient(to right, ${PLAYBAR_GREEN}, ${PLAYBAR_BLUE})`
  const toPercent = (t: number) => (duration ? Math.min(100, Math.max(0, (t / duration) * 100)) : 0)
  const playedPercent = toPercent(seekingTime ?? currentTime)
  const loadedPercent = Math.max(playedPercent, toPercent(bufferedEnd))
  const progressInputStyle: React.CSSProperties = {
    ...styles.progressInput,
    // The thumb, which Chrome paints in the accent colour: the old red fought
    // the gradient. Safari already drew it white, which is what Gavin saw.
    accentColor: '#FFFFFF',
    background: playbar === 'fill'
      ? [
          // Played: a layer only as wide as the played part, whose gradient
          // is stretched so its blue stop lands at the far end of the WHOLE
          // bar (a stop past 100% of a narrow layer). That keeps the colours
          // where the swatch puts them rather than squeezing green-to-blue
          // into whatever has played so far.
          `linear-gradient(to right, ${PLAYBAR_GREEN} 0%, ${PLAYBAR_BLUE} ${10000 / Math.max(playedPercent, 0.01)}%) 0 0 / ${playedPercent}% 100% no-repeat`,
          // Loaded: a light band from the start to the buffer end, visible
          // only past the playhead since the played layer covers the rest.
          `linear-gradient(rgba(255,255,255,0.45), rgba(255,255,255,0.45)) 0 0 / ${loadedPercent}% 100% no-repeat`,
          // Not loaded: the dim track.
          'rgba(255,255,255,0.18)',
        ].join(', ')
      : playbarGradient,
  }

  const formatTime = (timeInSeconds: number): string => {
    if (isNaN(timeInSeconds) || !isFinite(timeInSeconds)) return '0:00'
    const minutes = Math.floor(timeInSeconds / 60)
    const seconds = Math.floor(timeInSeconds % 60)
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  // In non-fill mode the video container normally gets its height from its
  // own width via aspect-ratio — so opening the transcript (narrower video
  // column) would shrink the WHOLE component. Instead, pin the aspect ratio
  // on the full-width row and letterbox the video inside it: total height
  // stays identical with the transcript open or closed.
  const letterboxTranscript = sidebarOpen && !(fillHeight || isFullscreen)

  // In grid view the video is pinned to the right edge of the player and the
  // transcript column extends to meet it, rather than a strip of black sitting
  // between the two. Nothing is repositioned to achieve that: the panel simply
  // takes the strip, which leaves the column exactly the video's width, and
  // the flat surface's existing centring then puts it flush right of its own
  // accord. Sphere views keep the ordinary sidebar width — the sphere fills
  // its container, so it has no strip to give away.
  //
  // The wrapper's pinned aspect ratio (letterboxTranscript, above) is what
  // holds the player's total height, and it does not depend on how the width
  // is split — so opening the transcript still cannot change the height.
  const transcriptColumnWidth = kind === 'flat'
    ? gridSidebarWidth(layout.sidebarWidth, layout.wrapperW, layout.columnH, videoAspect ?? 0)
    : layout.sidebarWidth

  // Popup menus must stay inside the player box — an iframed embed can be
  // barely taller than the control bar. Height budget: whatever the video
  // column has minus the controls the menu sits above.
  const menuMaxHeight = Math.max(160, layout.columnH - CONTROLS_HEIGHT_ALLOWANCE)
  // A split half's View menu drops from under its compass and camera button, so
  // its room is what is left between those and the bar.
  const paneMenuMaxHeight = Math.max(
    140,
    layout.columnH - controlsHeight - CLUSTER_INSET - clusterFit.compassSize - rowFit.buttonSize - 24,
  )
  // What each of the three quality words actually asks the player for. Pure
  // and tested in qualityTiers.ts; recomputed only when the ladder changes.
  const tierTarget = useMemo(() => tierTargets(qualityLevels), [qualityLevels])

  const changeTranscriptLanguage = (code: string) => {
    changeLang(code === normalizedMasterLanguage ? masterLanguage : code)
  }

  // Single click (no drag, no menu open, not on a control) toggles playback,
  // mirroring the Space key. Drags — e.g. rotating the 360 view — don't.
  //
  // A tap that lands while a menu is open only closes the menu (Gavin, 3 Nov:
  // tapping the video to get rid of the Subtitles list stopped the video).
  // Whether a menu was open is read at pointerdown, before the dismiss
  // listener below has closed it — by pointerup it is already gone, and the
  // tap used to read as an ordinary play/pause.
  //
  // Likewise a tap that lands while the controls are faded out only brings
  // them back, as on YouTube (3 Nov): on a phone you tap to find the
  // controls, and that tap used to pause the video as well.
  const clickStartRef = useRef<{ x: number; y: number; t: number; menuWasOpen: boolean } | null>(null)
  const handleSurfacePointerDown = (e: React.PointerEvent) => {
    const menuWasOpen = showSubtitlesMenu || showSettingsMenu || showViewMenu || paneMenu !== null
    clickStartRef.current = { x: e.clientX, y: e.clientY, t: Date.now(), menuWasOpen }
  }
  const handleSurfacePointerUp = (e: React.PointerEvent) => {
    const start = clickStartRef.current
    clickStartRef.current = null
    if (!start) return
    if ((e.target as HTMLElement).closest('button, input, a, [data-player-controls]')) return
    if (Math.hypot(e.clientX - start.x, e.clientY - start.y) > 8) {
      // A drag means the user is taking manual control of the camera —
      // stop the follow-speaker rotation fighting them. In split screen that
      // is the camera of whichever half the drag started in.
      if (kind === 'sphere' && !locked) {
        const canvas = e.currentTarget.querySelector('canvas')?.getBoundingClientRect()
        const inRightHalf = splitActive && !!canvas && start.x > canvas.left + canvas.width / 2
        if (inRightHalf) setSplitAutoRotate(false)
        else setAutoRotate(false)
      }
      return
    }
    if (Date.now() - start.t > 500) return
    if (tapWokeUiRef.current) return
    if (start.menuWasOpen || showSubtitlesMenu || showSettingsMenu || showViewMenu || paneMenu !== null) return
    if (isPlaying) coreRef.current?.pause()
    else if (!halted) coreRef.current?.play()
  }

  return (
    <div
      ref={wrapperRef}
      style={{
        ...((fillHeight || isFullscreen) ? styles.playerWrapperFill : styles.playerWrapper),
        ...(pseudoFullscreen
          ? { position: 'fixed', inset: 0, width: '100vw', height: '100dvh', zIndex: 2147483000, backgroundColor: '#000' }
          : {}),
        // Everything inside inherits it: menus, title, subtitles, transcript.
        // The time labels keep their own monospace, so the digits don't jitter.
        fontFamily: PLAYER_FONT,
        ...(sidebarOpen ? { display: 'flex', flexDirection: 'row' as const } : {}),
        ...(letterboxTranscript
          ? { aspectRatio: '4 / 3', minHeight: compact ? '200px' : '300px' }
          : {}),
      }}
    >
      {sidebarOpen && (
        /* The panel column must contribute NO intrinsic height — the row's
           height comes from the video column alone, and the panel fills it
           via an absolutely positioned inner box. Otherwise a long cue list
           stretches the whole layout. */
        <div style={{ width: transcriptColumnWidth, flexShrink: 0, position: 'relative', overflow: 'hidden', alignSelf: 'stretch' }}>
          <div style={{ position: 'absolute', inset: 0 }}>
            <TranscriptPanel
              variant="sidebar"
              cues={cues}
              currentCueIndex={currentCueIndex}
              participants={participants}
              speakerAttributions={speakerAttributions || {}}
              onSeekToCue={(cueIndex) => {
                coreRef.current?.seek(cues[cueIndex].startTime)
              }}
              onClose={() => setShowTranscript(false)}
              languages={transcriptLanguages}
              selectedLanguage={normalizedSelectedLang}
              onChangeLanguage={changeTranscriptLanguage}
            />
          </div>
        </div>
      )}
      <div
        style={{
          ...((fillHeight || isFullscreen || letterboxTranscript) ? { height: '100%' } : undefined),
          ...(sidebarOpen ? { flex: 1, minWidth: 0 } : {}),
        }}
      >
        <div
          ref={containerRef}
          onPointerDown={handleSurfacePointerDown}
          onPointerUp={handleSurfacePointerUp}
          style={{
            position: 'relative',
            ...(fillHeight || isFullscreen || letterboxTranscript
              ? styles.canvasContainerFill
              : compact
                ? styles.canvasContainerCompact
                : styles.canvasContainer),
            ...(uiHidden ? { cursor: 'none' } : {}),
          }}
        >
          {/* The core's video element lives in here for the whole mount and
              never moves. In sphere mode it is a 1px transparent texture source
              (iOS Safari refuses to texture a display:none video); in the flat view
              mode it fills the container and FlatSurface positions the player
              element against it — hence the relative/overflow/black styling,
              which the old FlatSurface container used to carry. */}
          <div
            ref={videoHostRef}
            style={kind === 'flat'
              ? { position: 'relative', width: '100%', height: '100%', backgroundColor: '#000', overflow: 'hidden' }
              // iPhone and iPad (QualityReadout.tsx, bigVideoHost): the hidden
              // element at the top rung's own size, so Safari's own HLS player
              // is not held to the rung that fits the player box.
              : bigVideoHost ? { ...styles.hiddenVideo, width: 3840, height: 1920 } : styles.hiddenVideo}
          />

          {/* Presentation layer. Both surfaces are pure presentation over the
              one core: mounting and unmounting them never touches playback. */}
          {core && (kind === 'flat' ? (
            <FlatSurface key="flat" core={core} containerRef={videoHostRef} />
          ) : (
            <Sphere360Surface
              key="sphere"
              ref={sphereRef}
              core={core}
              initialCamera={cameraRef.current}
              rotationAdjustment={rotationAdjustment}
              fillHeight={fillHeight || isFullscreen || letterboxTranscript}
              compact={compact}
              layout={paneLayout}
              paneHeadings={paneHeadings}
              fourView={fourViewFraming}
              onSplitCameraUpdate={(y, _p, f, fv) => setSplitCamera({ yaw: y, fov: f, fovVertical: fv })}
              onSplitViewPresetChange={setSplitViewPreset}
              onCameraUpdate={(y, p, f, fv) => {
                setCurrentYaw(y)
                setCurrentPitch(p)
                setCurrentFov(f)
                setCurrentFovVertical(fv)
                // Remember where the viewer is looking so a trip through the
                // flat view comes back to the same heading.
                cameraRef.current = { yaw: y, pitch: p }
              }}
              onViewPresetChange={setCurrentViewPreset}
              onGyroAvailabilityChange={setGyroAvailable}
              onGyroStateChange={setIsGyroEnabled}
              onFullscreenChange={setNativeFullscreen}
              onContextLost={() => { setTextureLive(false); if (kind === 'sphere') changeView('flat') }}
            />
          ))}

          {/* Terminal verification failure — the scrim. Bytes arrived, were
              hashed, and disagreed with what the publisher sealed, so the
              session is over: the surface fades to near-black and stays there.
              The last frame we could not vouch for is left faintly visible
              rather than blacked out, because the video did not go away — we
              stopped being able to check it.

              It carries no words. The account of what happened is in the
              verification panel, which a halt forces open above this
              (zIndex 12 vs 11) and which cannot then be closed. This replaces
              the full-screen VerificationFailure card that used to say all of
              it here, so there is now one explanation surface rather than two
              that could disagree.

              pointerEvents stays off: the scrim is above the controls overlay
              (zIndex 10), and pause and seek must still be reachable through
              it — the halt takes play away, not everything. Deliberately NOT
              part of the idle fade: it must not disappear because the viewer
              stopped moving the mouse. */}
          {halted && (
            <div
              aria-hidden
              style={{
                position: 'absolute',
                inset: 0,
                zIndex: 11,
                pointerEvents: 'none',
                ...failureScrimStyle({ visible: scrimVisible, reducedMotion: prefersReducedMotion() }),
              }}
            />
          )}

          {/* Stream error overlay */}
          {streamError && (
            <div style={{
              position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
              zIndex: 6, maxWidth: '80%', padding: '16px 20px',
              backgroundColor: 'rgba(0,0,0,0.85)', borderRadius: 8,
              color: '#ff8a80', fontSize: '0.9rem', textAlign: 'center', lineHeight: 1.5,
            }}>
              <div style={{ color: '#fff', fontWeight: 'bold', marginBottom: 8 }}>Stream failed to load</div>
              {streamError}
            </div>
          )}

          {/* Degraded-flat notice: the sphere is unavailable, so the 360°
              picture is shown flat */}
          {kind === 'flat' && (
            <div data-player-controls style={{
              position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)',
              zIndex: 6, padding: '6px 12px', maxWidth: '90%',
              backgroundColor: 'rgba(0,0,0,0.7)', borderRadius: 6,
              color: '#fff', fontSize: '0.8rem', textAlign: 'center',
              display: 'flex', alignItems: 'center', gap: 10,
              ...uiFadeStyle,
            }}>
              {staticLikely360(baseCaps) ? (
                <>
                  <span>360° view was disabled on this device</span>
                  <button
                    onClick={retry360}
                    style={{
                      padding: '2px 10px', background: 'rgba(255,255,255,0.15)',
                      border: '1px solid rgba(255,255,255,0.4)', borderRadius: 4,
                      color: '#fff', fontSize: '0.8rem', whiteSpace: 'nowrap',
                      cursor: 'pointer',
                    }}
                  >
                    Try 360°
                  </button>
                </>
              ) : (
                <span>360° view isn't supported on this device</span>
              )}
            </div>
          )}

          {/* Buffering spinner — a view switch reuses the same pipeline, so the
              only thing left to cover is real buffering. */}
          {!streamError && isBuffering && (
            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 5, pointerEvents: 'none' }}>
              <div style={{
                width: 48, height: 48, border: '4px solid rgba(255,255,255,0.3)',
                borderTopColor: '#fff', borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
              }} />
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          )}

          {/* Top-right: the compass alone. View / Follow / Gyro, which used to
              stand under it as words, are in the camera button's menu.
              Split screen draws one cluster per half — that half's compass,
              a size down, and under it an camera button holding that half's own
              Follow and View, which leave the bar's menu while split is on. */}
          {flags.compass && !locked && (splitActive ? PANES : PANES.slice(0, 1)).map((pane) => {
            const isRight = pane === 1
            const camera = isRight
              ? splitCamera
              : { yaw: currentYaw, fov: currentFov, fovVertical: currentFovVertical }
            const preset = isRight ? splitViewPreset : currentViewPreset
            const follow = isRight ? splitAutoRotate : autoRotate
            const half = isRight ? 'right half' : 'left half'
            // The dropdown sits on the radar's centre line, not its right edge.
            const centred = VIEW_BUTTON === 'menu' && clusterFit.direction === 'column'
            const viewLabel = VIEW_ROWS.find((row) => row[3] === preset)?.[1] ?? 'View'
            return (
              <div
                key={pane}
                data-player-controls
                style={{
                  ...styles.topRightCluster,
                  ...(splitActive && !isRight ? { right: `calc(50% + ${CLUSTER_INSET}px)` } : {}),
                  // An open menu hangs down towards the bar, so it has to
                  // clear the controls overlay.
                  ...(paneMenu === pane ? { zIndex: 20 } : {}),
                  flexDirection: clusterFit.direction === 'row' ? 'row-reverse' : 'column',
                  alignItems: clusterFit.direction === 'row' ? 'flex-start' : centred ? 'center' : 'flex-end',
                  ...uiFadeStyle,
                }}
              >
                <CompassWithParticipants
                  yaw={camera.yaw}
                  fov={camera.fov}
                  fovVertical={camera.fovVertical}
                  // Read off the camera rather than the View menu: zooming far
                  // enough out flips to the above view by itself.
                  overhead={preset !== 'default'}
                  participants={participants}
                  activeSpeaker={currentCue?.speaker || null}
                  onClickAngle={(compassDegrees) => {
                    sphereRef.current?.rotateTo((360 - compassDegrees) % 360, true, pane)
                  }}
                  parentHeight={layout.columnH}
                  size={clusterFit.compassSize}
                  radarImage={radarImage}
                />
                {splitActive && (flags.viewPresets || showFollow) && (
                  <div style={{ position: 'relative' }} ref={(el) => { paneMenuRefs.current[pane] = el }}>
                    {VIEW_BUTTON === 'menu' ? (
                    <button
                      // Gavin, 22 Oct: no eye, just the dropdown — a slab in
                      // the menu's own paint naming what this half shows, with
                      // the follow compass lit in front while it follows.
                      style={{
                        ...controlButton,
                        minWidth: 0,
                        minHeight: 0,
                        height: 32,
                        padding: '0 6px 0 12px',
                        gap: 6,
                        background: skinTokens.menuBackground,
                        borderRadius: skinTokens.menuRadius,
                        fontSize: skinTokens.rowFontSize,
                        fontFamily: PLAYER_FONT,
                        whiteSpace: 'nowrap',
                        ...openButton(paneMenu === pane),
                      }}
                      onClick={() => {
                        setPaneMenu((prev) => (prev === pane ? null : pane))
                        setShowViewMenu(false)
                        setShowSubtitlesMenu(false)
                        setShowSettingsMenu(false)
                      }}
                      aria-label={`Camera view, ${half}`}
                      aria-haspopup="menu"
                      aria-expanded={paneMenu === pane}
                    >
                      {follow && <PlayerGlyph name="follow" scale={0.7} style={{ color: skinTokens.openIcon }} />}
                      <span style={{ color: '#fff' }}>{flags.viewPresets ? viewLabel : 'Follow'}</span>
                      <ArrowDropDownIcon style={{ fontSize: 20, transform: paneMenu === pane ? 'rotate(180deg)' : 'none' }} />
                    </button>
                    ) : (
                    <button
                      // v4 (Gavin, 19 Oct): a black eye on a lit grey disc,
                      // drawn whole by ViewEyeDisc. v3 (11 Oct): the eye on
                      // the radar's own gradient, clear in the middle and
                      // white at the rim, so the edge draws the circle. White
                      // on white vanished against sky (9 Oct), so that eye
                      // keeps a dark edge to stand on.
                      style={{
                        ...controlButton,
                        ...(VIEW_BUTTON === 'v3' ? { background: CONE_DISC } : {}),
                        borderRadius: '50%',
                        ...openButton(paneMenu === pane),
                      }}
                      onClick={() => {
                        setPaneMenu((prev) => (prev === pane ? null : pane))
                        setShowViewMenu(false)
                        setShowSubtitlesMenu(false)
                        setShowSettingsMenu(false)
                      }}
                      aria-label={`Camera view, ${half}`}
                      aria-expanded={paneMenu === pane}
                    >
                      {VIEW_BUTTON === 'v4' ? (
                        // 80% of the button, which keeps its full hit area
                        // (Kai, 19 Oct: the full size read slightly big).
                        <ViewEyeDisc size={Math.round(rowFit.buttonSize * 0.8)} />
                      ) : (
                        // The shadow is on a wrapper: a filter on the glyph
                        // itself would be cut away by its own mask.
                        <span style={{ display: 'flex', filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.8))' }}>
                          {/* A size up from the bar's eye, to fill the disc
                              (Gavin, 11 Oct). */}
                          <PlayerGlyph name="view" scale={1.25} />
                        </span>
                      )}
                    </button>
                    )}
                    {paneMenu === pane && (
                      <div
                        // Under the button, not over it: this one hangs from
                        // the top of the picture, not off the bar.
                        style={{
                          ...menuSlab,
                          bottom: 'auto',
                          top: '100%',
                          marginBottom: 0,
                          marginTop: 8,
                          minWidth: 180,
                          maxHeight: paneMenuMaxHeight,
                          ...(centred ? { left: '50%', right: 'auto', transform: 'translateX(-50%)' } : {}),
                        }}
                        role="menu"
                        aria-label={`Camera view, ${half}`}
                      >
                        {showFollow && (
                          <button
                            style={menuHeadingRow(follow)}
                            role="menuitemcheckbox"
                            aria-checked={follow}
                            onClick={() => {
                              // One half at a time: two halves following would
                              // both turn to the same speaker, and switching
                              // one on read as switching both (Gavin, 9 Oct).
                              if (isRight) {
                                if (!splitAutoRotate) {
                                  lastSplitAutoRotatedSpeaker.current = ''
                                  setAutoRotate(false)
                                }
                                setSplitAutoRotate(!splitAutoRotate)
                              } else {
                                if (!autoRotate) {
                                  lastAutoRotatedSpeaker.current = ''
                                  setSplitAutoRotate(false)
                                }
                                setAutoRotate(!autoRotate)
                              }
                            }}
                          >
                            <span>
                              <PlayerGlyph name="follow" scale={0.75} style={{ marginRight: 10, verticalAlign: 'middle' }} />
                              Follow
                            </span>
                            <span style={{ fontWeight: 400, opacity: 0.85 }}>{follow ? 'On' : 'Off'}</span>
                          </button>
                        )}
                        {flags.viewPresets && (
                          <>
                            <div style={sectionHeading}>View</div>
                            {VIEW_ROWS.map(([v, label, Icon, rowPreset]) => {
                              const active = isRight ? splitViewPreset === rowPreset : view === v
                              return (
                                <button
                                  key={v}
                                  style={menuRow(active)}
                                  role="menuitemradio"
                                  aria-checked={active}
                                  onClick={() => {
                                    if (isRight) sphereRef.current?.setViewPreset(rowPreset, 1)
                                    else changeView(v)
                                    setPaneMenu(null)
                                  }}
                                >
                                  <span><Icon style={menuIcon} />{label}</span>
                                  {active && <span style={tick}>✓</span>}
                                </button>
                              )
                            })}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}

          {/* Programme title, top-left. Shares uiFadeStyle with the control
              bar, so it is up whenever the bar is up and gone when it is. */}
          <TitleOverlay series={series} episode={episode} name={name} fadeStyle={uiFadeStyle} />

          {/* ?zoominfo=1 only: the measuring panel for the Safari-vs-Chrome
              zoom-out question. Outside the idle fade, so it is still there
              when the screenshot is taken. */}
          {zoomReadoutEnabled && <ZoomReadout read={readZoomDiagnostics} />}
          {/* ?qualityinfo=1 only: which rung is really playing on a phone. */}
          {qualityReadoutEnabled && <QualityReadout core={core} surface={kind} />}

          {/* Podcast mode: in portrait containers the transcript covers the
              whole player, over the surface but under the controls overlay
              (zIndex 10), so pause/seek stay reachable through the bottom bar.
              Audio keeps playing underneath — nothing about the pipeline
              changes. data-player-controls stops the surface click handler
              from toggling playback on every cue tap. */}
          {overlayOpen && (
            <div
              data-player-controls
              style={{ position: 'absolute', inset: 0, zIndex: 9 }}
            >
              <TranscriptPanel
                variant="overlay"
                cues={cues}
                currentCueIndex={currentCueIndex}
                participants={participants}
                speakerAttributions={speakerAttributions || {}}
                onSeekToCue={(cueIndex) => {
                  coreRef.current?.seek(cues[cueIndex].startTime)
                }}
                onClose={() => setShowTranscript(false)}
                languages={transcriptLanguages}
                selectedLanguage={normalizedSelectedLang}
                onChangeLanguage={changeTranscriptLanguage}
              />
            </div>
          )}

          {/* Subtitle overlay — always bottom-center for both surfaces; shows
              alongside the transcript sidebar; suppressed in podcast mode
              (redundant with the highlighted transcript cue); hidden mid-swap */}
          {subtitlesVisible && currentCue && !overlayOpen && (
            <SubtitleOverlay
              currentCue={currentCue}
              participants={participants}
              fontSize={subtitleFontSize}
              controlsVisible={!uiHidden}
              controlsHeight={controlsHeight}
              activeTrack={activeSubtitleTrack}
            />
          )}

          {/* Verification overlay — the modal the glyph opens. A sibling of
              the controls overlay and above it (zIndex 12), so fullscreen
              keeps it (fullscreen targets this component's wrapper) while the
              idle fade never touches it: the point of the panel is that a
              viewer can go and look, and it must not evaporate because they
              stopped moving the mouse. Rendered only when the slot returns
              something, so a closed modal covers nothing and eats no clicks. */}
          {verificationOverlayNode && (
            <div
              data-player-controls
              style={{
                position: 'absolute',
                inset: 0,
                zIndex: 12,
                // The LAYER catches nothing; the panel inside it catches
                // everything. A viewer watching the chart must still be able to
                // scrub, pause and change quality — seeking is the most
                // interesting thing they can do to it, because the coverage
                // then visibly grows islands rather than a single run.
                pointerEvents: 'none',
              }}
            >
              {verificationOverlayNode}
            </div>
          )}

          {/* Controls Overlay */}
          <div ref={controlsOverlayRef} data-player-controls style={{ ...styles.controlsOverlay, ...uiFadeStyle }}>
            {/* Scrub row — elapsed time, the bar, total time. */}
            <div style={styles.scrubRow}>
              <span style={timeLabelStyle}>{formatTime(seekingTime ?? currentTime)}</span>
              <div
                style={styles.progressBarContainer}
                onPointerMove={(e) => {
                  // Mouse only, and never before a duration is known: a readout
                  // computed against `0` would say 0:00 the whole way along.
                  if (e.pointerType !== 'mouse' || !duration) return
                  const rect = e.currentTarget.getBoundingClientRect()
                  if (!rect.width) return
                  const x = Math.min(Math.max(e.clientX - rect.left, 0), rect.width)
                  setScrubHover({ time: (x / rect.width) * duration, x })
                }}
                onPointerLeave={() => setScrubHover(null)}
              >
                <input
                  type="range"
                  min={0}
                  max={duration || 1}
                  step={0.1}
                  value={seekingTime ?? currentTime}
                  onMouseDown={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect()
                    const offsetX = e.clientX - rect.left
                    const width = rect.width
                    const newTime = (offsetX / width) * (duration || 1)
                    setSeekingTime(newTime)
                    seekingTimeRef.current = newTime
                  }}
                  onTouchStart={(e) => {
                    const touch = e.touches[0]
                    const rect = e.currentTarget.getBoundingClientRect()
                    const offsetX = touch.clientX - rect.left
                    const width = rect.width
                    const newTime = (offsetX / width) * (duration || 1)
                    setSeekingTime(newTime)
                    seekingTimeRef.current = newTime
                  }}
                  onInput={(e) => {
                    const newTime = parseFloat(e.currentTarget.value)
                    setSeekingTime(newTime)
                    seekingTimeRef.current = newTime
                  }}
                  onMouseUp={(e) => {
                    const newTime = parseFloat(e.currentTarget.value)
                    coreRef.current?.seek(newTime)
                  }}
                  onTouchEnd={(e) => {
                    const newTime = parseFloat(e.currentTarget.value)
                    coreRef.current?.seek(newTime)
                  }}
                  style={progressInputStyle}
                  aria-label="Seek slider"
                />
                {/* Follows the pointer along the bar. `pointerEvents: none` in
                    scrubTooltip keeps it clear of drag-to-seek. */}
                {scrubHover && (
                  <div style={{ ...styles.scrubTooltip, left: `${scrubHover.x}px` }}>
                    {formatTime(scrubHover.time)}
                  </div>
                )}
              </div>
              <span style={timeLabelStyle}>{formatTime(duration)}</span>
            </div>

            {/* Controls Row — three zones (LEFT / CENTRE / RIGHT), the BBC
                skeleton. The old flex spacer is gone: it could not hold a
                true centre, because the play button's position followed the
                right cluster's width and so drifted between videos. */}
            <div style={{ ...styles.controlsRow, ...rowSpread, gap: `${rowFit.rowGap}px`, height: `${rowFit.buttonSize + 8}px` }}>
              {/* LEFT zone — 3D audio, then volume: "you keep your sound on your
                  left", with the 3D toggle just outside it. */}
              <div style={{ ...styles.controlZoneLeft, ...sideZone }}>
                {/* 3D Audio — between play and volume (sphere views, requires participants).
                    White off, the skin's green on (Kai, 30 Sep evening): it has
                    no menu, so "on" is its selected state. It used to dim to
                    0.6 when off, which read as grey. */}
                {flags.spatialAudio && participants.length > 0 && splitLayout === 1 && (
                  <Tooltip slotProps={tooltipSlots} title={spatialAudio ? 'Disable 3D audio' : 'Enable 3D audio'} placement="top">
                    <button
                      style={{ ...controlButton, ...openButton(spatialAudio) }}
                      onClick={() => {
                        const next = !spatialAudio
                        setSpatialAudio(next)
                        if (kind === 'sphere') {
                          sphereRef.current?.setSpatialAudioEnabled(next)
                        }
                      }}
                      aria-label={spatialAudio ? 'Disable 3D audio' : 'Enable 3D audio'}
                    >
                      {spatialAudio ? <SpatialAudioIcon fontSize="medium" /> : <SpatialAudioOffIcon fontSize="medium" />}
                    </button>
                  </Tooltip>
                )}

                {/* Volume — the slider goes on narrow columns (phones have
                    hardware volume); the mute button always stays. */}
                <div style={styles.volumeContainer}>
                  <button
                    style={controlButton}
                    onClick={() => coreRef.current?.toggleMute()}
                    aria-label={isMuted ? 'Unmute' : 'Mute'}
                  >
                    {isMuted || volume === 0 ? (
                      <VolumeOffIcon fontSize="medium" />
                    ) : (
                      <VolumeUpIcon fontSize="medium" />
                    )}
                  </button>
                  {rowFit.showVolumeSlider && (
                    <input
                      type="range"
                      min={0}
                      max={1}
                      step={0.05}
                      value={shownVolume}
                      onInput={(e) => {
                        const newVolume = parseFloat(e.currentTarget.value)
                        coreRef.current?.setVolume(newVolume)
                      }}
                      onChange={(e) => {
                        const newVolume = parseFloat(e.target.value)
                        coreRef.current?.setVolume(newVolume)
                      }}
                      style={{
                        ...styles.volumeSlider,
                        // The scrub bar's green-to-blue, filled to the thumb;
                        // the gradient spans the whole slider, as it does there.
                        background: [
                          `linear-gradient(to right, ${PLAYBAR_GREEN} 0%, ${PLAYBAR_BLUE} ${100 / Math.max(shownVolume, 0.01)}%) 0 0 / ${shownVolume * 100}% 100% no-repeat`,
                          'rgba(255,255,255,0.3)',
                        ].join(', '),
                      }}
                      aria-label="Volume slider"
                    />
                  )}
                </div>
              </div>

              {/* CENTRE zone — skip back 10, play/pause, skip forward 10. It
                  holds the player's true midline only because the left and right
                  zones carry an identical flex basis, so the centre does not
                  drift as the right cluster gains and loses the verification
                  glyph and the 360 button between videos. */}
              <div style={styles.controlZoneCentre}>
                {/* Skip back 10s — same distance as ArrowLeft, so the icon
                    tells the truth whichever input the viewer reaches for.
                    Both skips go on a column too narrow for them (rowFit). */}
                {rowFit.showSkip && (
                <Tooltip slotProps={tooltipSlots} title="Back 10 seconds" placement="top">
                  <span style={{ display: 'flex' }}>
                    <button
                      style={controlButton}
                      onClick={() => skipBy(-SKIP_SECONDS)}
                      disabled={halted && !isPlaying}
                      aria-label="Back 10 seconds"
                    >
                      <Replay10Icon fontSize="large" />
                    </button>
                  </span>
                </Tooltip>
                )}

                {/* Play/Pause */}
                <button
                  style={controlButton}
                  onClick={() => {
                    sdbg('play/pause button clicked', { isPlaying, kind, halted })
                    if (isPlaying) {
                      coreRef.current?.pause()
                    } else if (!halted) {
                      coreRef.current?.play()
                    }
                  }}
                  disabled={halted && !isPlaying}
                  aria-label={isPlaying ? 'Pause' : 'Play'}
                >
                  {isPlaying ? <PauseIcon fontSize="large" /> : <PlayArrowIcon fontSize="large" />}
                </button>

                {/* Skip forward 10s — mirrors ArrowRight. */}
                {rowFit.showSkip && (
                <Tooltip slotProps={tooltipSlots} title="Forward 10 seconds" placement="top">
                  <span style={{ display: 'flex' }}>
                    <button
                      style={controlButton}
                      onClick={() => skipBy(SKIP_SECONDS)}
                      disabled={halted && !isPlaying}
                      aria-label="Forward 10 seconds"
                    >
                      <Forward10Icon fontSize="large" />
                    </button>
                  </span>
                </Tooltip>
                )}
              </div>

              {/* RIGHT zone: verification glyph, 360, Follow, camera, CC,
                  settings gear, fullscreen, in that order (Gavin, 17 Sep mock;
                  360 moved up 10 Oct) —
                  the glyph stays first, for the reason its own comment below
                  gives. The time labels flank the scrub bar above, and quality
                  is three words inside the gear's menu. */}
              <div style={{ ...styles.controlZoneRight, ...sideZone }}>
                {/* Verification glyph — FIRST in the right-hand cluster. KEPT at
                    every width: the volume slider drops under fitControlRow
                    because it is a convenience, and this is not one. Rendered
                    outside the `subtitleUrl` guard below, so a video with no
                    subtitles still shows it. */}
                {verificationControlNode && (
                  <Tooltip slotProps={tooltipSlots} title={verificationOverlayNode ? '' : 'Verification'} placement="top">
                    <span style={{ display: 'flex' }}>{verificationControlNode}</span>
                  </Tooltip>
                )}

                {/* 360 button — flat view only; returns to last sphere view.
                    Straight after the glyph, between verification and
                    subtitles (Gavin, 10 Oct); it was last but one. */}
                {flags.switchTo360 && (
                  <Tooltip slotProps={tooltipSlots} title="Back to 360°" placement="top">
                    <button
                      style={controlButton}
                      onClick={() => (textureLive ? changeView(lastSphereViewRef.current) : retry360())}
                      aria-label="Back to 360°"
                    >
                      <PlayerGlyph name="threeSixty" />
                    </button>
                  </Tooltip>
                )}

                {/* The camera menu, behind Gavin's eye (11 Oct; a compass for
                    the day before). The three views, then Grid, as Gavin's mock orders
                    them; Gyro (phones only) comes last. These were the View / Follow / Gyro words under
                    the radar, and before that a MUI menu hanging off "View". */}
                {/* Follow — a button of its own (Gavin, 9 Oct); it was the row
                    heading the camera menu. Lit while on, as 3D audio is. In
                    split each half has its own Follow, under its compass. */}
                {showFollowButton && (
                  <Tooltip slotProps={tooltipSlots} title={autoRotate ? 'Stop following the speaker' : 'Follow the speaker'} placement="top">
                    <button
                      style={{ ...controlButton, ...openButton(autoRotate) }}
                      onClick={() => setAutoRotate((prev) => {
                        // Re-enabling should jump to whoever is talking right
                        // now, not wait for the next speaker change — clear
                        // the marker so the auto-rotate effect fires at once.
                        if (!prev) lastAutoRotatedSpeaker.current = ''
                        return !prev
                      })}
                      aria-label="Follow the speaker"
                      aria-pressed={autoRotate}
                    >
                      <PlayerGlyph name="follow" />
                    </button>
                  </Tooltip>
                )}

                {showViewButton && (
                  <div style={{ position: 'relative' }} ref={viewMenuRef}>
                    <Tooltip slotProps={tooltipSlots} title={showViewMenu ? '' : 'Camera view'} placement="top">
                      <button
                        style={{ ...controlButton, ...openButton(showViewMenu) }}
                        onClick={() => {
                          setShowViewMenu((prev) => !prev)
                          setShowSubtitlesMenu(false)
                          setShowSettingsMenu(false)
                        }}
                        aria-label="Camera view"
                        aria-expanded={showViewMenu}
                      >
                        <PlayerGlyph name="view" />
                      </button>
                    </Tooltip>
                    {showViewMenu && (
                      <div style={{ ...menuSlab, minWidth: 180, maxHeight: menuMaxHeight }} role="menu" aria-label="Camera view">
                        {flags.viewPresets && splitLayout === 1 && (
                          <>
                            {/* White, not the half-opacity of the other section
                                labels: Gavin, 30 Sep evening, "just seen View -
                                can you make that white please". His own mock of
                                the subtitle menu keeps Text size and Language
                                grey, so only this one changes. */}
                            <div style={sectionHeading}>View</div>
                            {VIEW_ROWS.map(([v, label, Icon]) => (
                              <button
                                key={v}
                                style={menuRow(view === v)}
                                role="menuitemradio"
                                aria-checked={view === v}
                                onClick={() => changeView(v)}
                              >
                                <span><Icon style={menuIcon} />{label}</span>
                                {view === v && <span style={tick}>✓</span>}
                              </button>
                            ))}
                          </>
                        )}
                        {/* The layouts, each row drawn as its own shape —
                            All views as the panes this video's people make.
                            All views locks every pane to a participant, so
                            View and Motion leave the menu. */}
                        {kind === 'sphere' && (
                          <>
                            <div style={{ ...sectionHeading, marginTop: splitLayout === 1 && flags.viewPresets ? 6 : 0 }}>Layout</div>
                            {layoutChoices.map((n) => (
                              <button
                                key={n}
                                style={menuRow(splitLayout === n)}
                                role="menuitemradio"
                                aria-checked={splitLayout === n}
                                onClick={() => chooseLayout(n)}
                              >
                                <span><LayoutGlyph layout={resolvePaneLayout(n, participants.length)} style={{ ...menuIcon, width: 18 }} />{LAYOUT_LABELS[n]}</span>
                                {splitLayout === n && <span style={tick}>✓</span>}
                              </button>
                            ))}
                          </>
                        )}
                        {showGyro && !locked && (
                          <button
                            style={menuRow(isGyroEnabled)}
                            role="menuitemcheckbox"
                            aria-checked={isGyroEnabled}
                            onClick={() => {
                              // Close the menu: on iOS this opens the motion
                              // permission prompt, which must not sit under it.
                              setShowViewMenu(false)
                              sphereRef.current?.toggleGyro()
                            }}
                          >
                            <span>
                              {isGyroEnabled ? <ScreenRotationIcon style={menuIcon} /> : <ScreenRotationOutlinedIcon style={menuIcon} />}
                              Motion
                            </span>
                            <span style={{ opacity: 0.85 }}>{isGyroEnabled ? 'On' : 'Off'}</span>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Subtitles (CC) menu — Gavin's 17 Sep mock: Transcript on/off,
                    Subtitles on/off, text size, then the language list. The
                    transcript and language used to be buttons of their own on
                    the bar; one language choice drives both the subtitles and
                    the transcript, so it lives once, here.

                    The Transcript row's condition is `cues.length > 0`, not
                    `flags.transcript`: that flag is true as soon as a subtitle
                    URL is supplied, before the file loads or when it fails to
                    parse, and the panel itself renders only with cues. */}
                {subtitleUrl && (
                  <div style={{ position: 'relative' }} ref={subtitlesMenuRef}>
                    <Tooltip slotProps={tooltipSlots} title={showSubtitlesMenu ? '' : 'Subtitles'} placement="top">
                      <button
                        style={{ ...controlButton, opacity: subtitlesVisible || showTranscript ? 1 : 0.6, ...openButton(showSubtitlesMenu) }}
                        onClick={() => {
                          setShowSubtitlesMenu((prev) => !prev)
                          setShowViewMenu(false)
                          setShowSettingsMenu(false)
                        }}
                        aria-label="Subtitle options"
                        aria-expanded={showSubtitlesMenu}
                      >
                        {subtitlesVisible ? (
                          <SubtitlesIcon fontSize="medium" />
                        ) : (
                          <ClosedCaptionDisabledIcon fontSize="medium" />
                        )}
                      </button>
                    </Tooltip>
                    {showSubtitlesMenu && (
                      <div style={{ ...menuSlab, minWidth: CC_MENU_WIDTH, maxHeight: menuMaxHeight }} role="menu" aria-label="Subtitle options">
                        {cues.length > 0 && (
                          <button
                            style={{ ...menuHeadingRow(showTranscript), marginBottom: 6 }}
                            role="menuitemcheckbox"
                            aria-checked={showTranscript}
                            onClick={() => setShowTranscript((prev) => !prev)}
                          >
                            <span>Transcript</span>
                            <span style={{ fontWeight: 400, opacity: 0.85 }}>{showTranscript ? 'On' : 'Off'}</span>
                          </button>
                        )}
                        <button
                          style={menuHeadingRow(subtitlesVisible)}
                          role="menuitemcheckbox"
                          aria-checked={subtitlesVisible}
                          onClick={() => setSubtitlesVisible((prev) => !prev)}
                        >
                          <span>Subtitles</span>
                          <span style={{ fontWeight: 400, opacity: 0.85 }}>{subtitlesVisible ? 'On' : 'Off'}</span>
                        </button>
                        {/* Text size scales the subtitle overlay only, not the
                            transcript panel. */}
                        <div style={sectionHeading}>Text size</div>
                        {SUBTITLE_SIZE_OPTIONS.map((option) => (
                          <button
                            key={option.preset}
                            style={menuRow(subtitleSizePreset === option.preset)}
                            role="menuitemradio"
                            aria-checked={subtitleSizePreset === option.preset}
                            onClick={() => changeSubtitleSizePreset(option.preset)}
                          >
                            <span>{option.label}</span>
                            {subtitleSizePreset === option.preset && <span style={tick}>✓</span>}
                          </button>
                        ))}
                        {/* Language — the same list the transcript panel's own
                            picker offers. Addenda carry their added-on date (or
                            "not yet on chain") in the label itself; see
                            transcriptLanguages above. */}
                        {showLanguageChoice && (
                          <>
                            <div style={sectionHeading}>Language</div>
                            {transcriptLanguages.map((l) => {
                              const active = (normalizedSelectedLang || normalizedMasterLanguage) === l.code
                              return (
                                <button
                                  key={l.code}
                                  style={menuRow(active)}
                                  role="menuitemradio"
                                  aria-checked={active}
                                  onClick={() => changeTranscriptLanguage(l.code)}
                                >
                                  <span>{l.label}</span>
                                  {active && <span style={tick}>✓</span>}
                                </button>
                              )
                            })}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Settings — video quality, as three words.

                    RENDERED FOR EVERY VIEWER, deliberately not behind
                    flags.qualitySelector: that flag is a capability gate (it
                    is `mse`, so false on iOS native HLS), and hiding the menu
                    there would take quality control away from exactly the
                    viewers who never had it. The browser is picking a rung
                    regardless; tierTargets resolves all three words to 'auto'
                    when no ladder is exposed, so every choice still tells the
                    truth and the gear is never an empty promise.

                    The real rendition ladder — 428x214, 1920x960 and the rest
                    — was a chip on this row. It was built for testing
                    playback, not for watching, so it now lives below, behind
                    ?debug=true. */}
                <div style={{ position: 'relative' }} ref={settingsMenuWrapRef}>
                  <Tooltip slotProps={tooltipSlots} title={showSettingsMenu ? '' : 'Settings'} placement="top">
                    <button
                      style={{ ...controlButton, ...openButton(showSettingsMenu) }}
                      onClick={() => {
                        setShowSettingsMenu((prev) => !prev)
                        setShowViewMenu(false)
                        setShowSubtitlesMenu(false)
                      }}
                      aria-label="Settings"
                      aria-expanded={showSettingsMenu}
                    >
                      <SettingsIcon fontSize="medium" />
                    </button>
                  </Tooltip>
                  {showSettingsMenu && (
                    <div
                      style={{
                        ...menuSlab,
                        // Wide enough for "Highest available" and its hint line.
                        minWidth: 220,
                        maxHeight: menuMaxHeight,
                      }}
                    >
                      <div style={menuSectionLabel}>Video quality</div>
                      {QUALITY_TIER_ORDER.map((tier) => (
                        <React.Fragment key={tier}>
                          <button
                            style={menuRow(selectedTier === tier)}
                            onClick={() => {
                              setSelectedTier(tier)
                              setSelectedQuality(tierTarget[tier])
                              coreRef.current?.setQualityLevel(tierTarget[tier])
                              setShowSettingsMenu(false)
                            }}
                          >
                            <span>{QUALITY_TIER_LABELS[tier]}</span>
                            {selectedTier === tier && <span style={tick}>✓</span>}
                          </button>
                          {tier === 'highest' && (
                            <div style={styles.settingsMenuHint}>
                              Automatically selects the best quality
                            </div>
                          )}
                        </React.Fragment>
                      ))}

                      {/* The full ladder, ?debug=true only. Pinning a rung by
                          hand is not a tier, so it clears the checkmark above
                          rather than leaving a word ticked that no longer
                          describes what is playing. */}
                      {swapDebug && qualityLevels.length > 0 && (
                        <>
                          <div style={styles.settingsMenuDivider} />
                          <div style={styles.subtitleMenuSectionLabel}>Renditions (debug)</div>
                          <button
                            style={menuRow(selectedQuality === 'auto')}
                            onClick={() => {
                              setSelectedTier('highest')
                              setSelectedQuality('auto')
                              coreRef.current?.setQualityLevel('auto')
                              setShowSettingsMenu(false)
                            }}
                          >
                            <span>
                              Auto{activeQualityIndex >= 0 && qualityLevels[activeQualityIndex] ? ` (${qualityLabel(qualityLevels[activeQualityIndex])})` : ''}
                            </span>
                            {selectedQuality === 'auto' && <span style={tick}>✓</span>}
                          </button>
                          {[...qualityLevels]
                            .sort((a, b) => b.width - a.width || b.height - a.height)
                            .map((level) => (
                              <button
                                key={level.index}
                                style={menuRow(selectedQuality === level.index)}
                                onClick={() => {
                                  setSelectedTier(null)
                                  setSelectedQuality(level.index)
                                  coreRef.current?.setQualityLevel(level.index)
                                  setShowSettingsMenu(false)
                                }}
                              >
                                <span>{qualityLabel(level)}</span>
                                {selectedQuality === level.index && <span style={tick}>✓</span>}
                              </button>
                            ))}
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Fullscreen — works in both sphere and flat views */}
                <Tooltip slotProps={tooltipSlots} title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'} placement="top">
                  <button
                    style={controlButton}
                    onClick={() => toggleFullscreen()}
                    aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                  >
                    {isFullscreen ? (
                      <FullscreenExitIcon fontSize="medium" />
                    ) : (
                      <FullscreenIcon fontSize="medium" />
                    )}
                  </button>
                </Tooltip>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  )
})

VideoPlayer.displayName = 'VideoPlayer'
export default VideoPlayer
